// components/common/CurateMatchesModal.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Send, Package, Inbox, Eye, Calendar, X, Clock, Plus, Trash2, Check,
  Search, ChevronDown, RefreshCw, MapPin
} from 'react-feather';
import AuthService from '../../services/authService';
import MapAreaPickerModal from './MapAreaPickerModal';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

// Preset option arrays — mirrored from the buyer-facing FilterMenu so agents
// see the same canonical buckets the buyer was offered. value === '' means "Any".
const MIN_SQFT_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '500', label: '500' },
  { value: '750', label: '750' },
  { value: '1000', label: '1,000' },
  { value: '1250', label: '1,250' },
  { value: '1500', label: '1,500' },
  { value: '2000', label: '2,000' },
  { value: '2500', label: '2,500' },
  { value: '3000', label: '3,000' },
  { value: '4000', label: '4,000' },
  { value: '5000', label: '5,000' },
];

const MAX_SQFT_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '1000', label: '1,000' },
  { value: '1500', label: '1,500' },
  { value: '2000', label: '2,000' },
  { value: '2500', label: '2,500' },
  { value: '3000', label: '3,000' },
  { value: '4000', label: '4,000' },
  { value: '5000', label: '5,000' },
  { value: '7500', label: '7,500' },
  { value: '10000', label: '10,000+' },
];

const YEAR_BUILT_OPTIONS = [
  { value: '', label: 'Any' },
  { value: 'new', label: 'New (≤2 yrs)' },
  { value: '2020', label: '2020+' },
  { value: '2010', label: '2010+' },
  { value: '2000', label: '2000+' },
  { value: '1990', label: '1990+' },
  { value: '1980', label: '1980+' },
  { value: '1970', label: '1970+' },
  { value: 'pre1970', label: 'Pre-1970' },
];

// Lot sizes in acres — buildInitialSearchForm divides backend sqft by 43560 to get acres
const MIN_LOT_SIZE_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '0.25', label: '0.25 acres' },
  { value: '0.5', label: '0.5 acres' },
  { value: '1', label: '1 acre' },
  { value: '2', label: '2 acres' },
  { value: '5', label: '5 acres' },
  { value: '10', label: '10 acres' },
  { value: '20', label: '20 acres' },
];

const MAX_LOT_SIZE_OPTIONS = [
  { value: '', label: 'Any' },
  { value: '0.5', label: '0.5 acres' },
  { value: '1', label: '1 acre' },
  { value: '2', label: '2 acres' },
  { value: '5', label: '5 acres' },
  { value: '10', label: '10 acres' },
  { value: '20', label: '20 acres' },
  { value: '50', label: '50 acres' },
  { value: '100', label: '100+ acres' },
];

/**
 * CurateMatchesModal
 *
 * Standalone modal for sending curated property matches to a user.
 * Owns all of its own state (properties, selected, view, pagination, etc.).
 *
 * Props:
 *   isOpen                 - bool: whether modal is visible
 *   onClose                - () => void: parent should hide modal
 *   buyer                  - object: the user to curate matches for (must have _id and name)
 *   onPropertyDetailsClick - (property) => void: parent opens its own PropertyDetailsModal
 *   onSuccess              - (msg) => void: optional, parent shows success toast
 *   onError                - (msg) => void: optional, parent shows error toast
 */
const CurateMatchesModal = ({
  isOpen,
  onClose,
  buyer,
  onPropertyDetailsClick = () => {},
  onSuccess = () => {},
  onError = () => {},
}) => {
  const [portalTarget, setPortalTarget] = useState(null);
  useEffect(() => { setPortalTarget(document.body); }, []);

  // Curate state (lifted in from AgentInvitations.js)
  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [isCurating, setIsCurating] = useState(false);
  const [existingMatches, setExistingMatches] = useState([]);
  const [matchHistory, setMatchHistory] = useState([]);
  const [curateView, setCurateView] = useState('create'); // 'create' | 'current' | 'history'
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [propertiesTotalPages, setPropertiesTotalPages] = useState(1);
  const [propertiesTotal, setPropertiesTotal] = useState(0);
  const [isLoadingMoreProperties, setIsLoadingMoreProperties] = useState(false);
  const [totalMatchesSent, setTotalMatchesSent] = useState(0);

  // SEARCH MODE state — used inside the Create New view when toggled to "Search All Listings"
  const [createMode, setCreateMode] = useState('algorithm'); // 'algorithm' | 'search'
  const [searchResults, setSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(1);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchTotalPages, setSearchTotalPages] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearchedYet, setHasSearchedYet] = useState(false);
  const [mlsAcknowledged, setMlsAcknowledged] = useState(false);
  const [mlsHighlighted, setMlsHighlighted] = useState(false); // brief flash when agent tries to search without acknowledging
  const [searchForm, setSearchForm] = useState(null); // initialized lazily from buyer.filterPreferences

  // Agent's claimed territories (cities) — fetched from /api/agent-pools/my-pools
  const [agentTerritories, setAgentTerritories] = useState([]); // [{ city, state }, ...]
  const [isLoadingTerritories, setIsLoadingTerritories] = useState(false);
  const [territoriesError, setTerritoriesError] = useState('');

  // City multi-select dropdown UI state
  const [citiesDropdownOpen, setCitiesDropdownOpen] = useState(false);
  const [cityFilterText, setCityFilterText] = useState('');
  const citiesDropdownRef = useRef(null);
  const resultsAnchorRef = useRef(null);

  // Map area picker — when set, the search uses POST /saved-areas/search with
  // this polygon instead of the GET /properties endpoint, giving the agent
  // a hand-drawn (or saved) area as the location filter.
  const [showMapPicker, setShowMapPicker] = useState(false);

  // ── Address lookup ──────────────────────────────────────────────────────────
  // Deliberately NOT part of searchForm. It is not a filter: it doesn't
  // serialize into the query string, doesn't participate in the cities/polygon
  // either-or, and outranks both. When a lookup runs, every filter below is
  // ignored — an agent typing a specific address wants that building, not that
  // building narrowed by the buyer's price range.
  const [addressQuery, setAddressQuery] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupNotice, setLookupNotice] = useState(null);   // { tone, text } | null
  const [lookupSource, setLookupSource] = useState(null);   // 'inventory' | 'mls' | null
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(-1);
  const suggestDebounceRef = useRef(null);
  const suggestSeqRef = useRef(0);        // guards against out-of-order responses
  const addressBoxRef = useRef(null);

  // Bootstrap when modal opens with a buyer
  useEffect(() => {
    if (isOpen && buyer?._id) {
      // Fresh load
      setSelectedProperties([]);
      setPropertySearchTerm('');
      setCurateView('create');
      setPropertiesPage(1);
      setPropertiesTotalPages(1);
      setPropertiesTotal(0);
      setProperties([]);
      setFilteredProperties([]);
      setTotalMatchesSent(0);

      // Reset search-mode state and seed form defaults from the buyer's filterPreferences
      setCreateMode('algorithm');
      setSearchResults([]);
      setSearchPage(1);
      setSearchTotal(0);
      setSearchTotalPages(0);
      setHasSearchedYet(false);
      setMlsAcknowledged(false);
      setSearchForm(buildInitialSearchForm(buyer));

      // Address lookup resets too — a stale address from the previous buyer
      // silently scoping this buyer's results would be a nasty bug.
      setAddressQuery('');
      setLookupNotice(null);
      setLookupSource(null);
      setSuggestions([]);
      setSelectedSuggestion(null);
      setHighlightedSuggestion(-1);
      setShowMapPicker(false);

      fetchExistingMatches(buyer._id);
      fetchPropertiesForUser(buyer._id, 1, false);
      fetchAgentTerritories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, buyer?._id]);

  // Close the address suggestion dropdown when clicking outside it
  useEffect(() => {
    if (suggestions.length === 0) return;
    const handleClickOutside = (e) => {
      if (addressBoxRef.current && !addressBoxRef.current.contains(e.target)) {
        setSuggestions([]);
        setHighlightedSuggestion(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [suggestions.length]);

  // Drop any pending suggestion fetch when the component goes away
  useEffect(() => () => clearTimeout(suggestDebounceRef.current), []);

  // Close cities dropdown when clicking outside it
  useEffect(() => {
    if (!citiesDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (citiesDropdownRef.current && !citiesDropdownRef.current.contains(e.target)) {
        setCitiesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [citiesDropdownOpen]);

  const fetchPropertiesForUser = async (userId, page = 1, append = false) => {
    try {
      setIsLoadingMoreProperties(true);
      const response = await fetch(`${API_URL}/properties/lauftalg/user/${userId}?page=${page}&limit=50`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      if (!response.ok) throw new Error(`API returned ${response.status}`);

      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        const newProperties = data.data;
        if (append) {
          setProperties(prev => [...prev, ...newProperties]);
          setFilteredProperties(prev => [...prev, ...newProperties]);
        } else {
          setProperties(newProperties);
          setFilteredProperties(newProperties);
        }
        setPropertiesPage(data.page);
        setPropertiesTotalPages(data.pages);
        setPropertiesTotal(data.total);
      } else if (!append) {
        setProperties([]);
        setFilteredProperties([]);
      }
    } catch (err) {
      console.error('Error fetching properties:', err);
      onError('Failed to load properties: ' + err.message);
    } finally {
      setIsLoadingMoreProperties(false);
    }
  };

  const loadMoreProperties = () => {
    if (buyer?._id && propertiesPage < propertiesTotalPages) {
      fetchPropertiesForUser(buyer._id, propertiesPage + 1, true);
    }
  };

  const fetchAgentTerritories = async () => {
    setIsLoadingTerritories(true);
    setTerritoriesError('');
    try {
      const response = await fetch(`${API_URL}/agent-pools/my-pools`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      if (!response.ok) throw new Error(`API returned ${response.status}`);
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        // Each pool has serviceArea: { city, state }. Pull out unique city+state pairs.
        const seen = new Set();
        const territories = [];
        for (const pool of data.data) {
          const city = pool.serviceArea?.city?.trim();
          const state = pool.serviceArea?.state?.trim();
          if (!city) continue;
          const key = `${city}|${state || ''}`.toLowerCase();
          if (seen.has(key)) continue;
          seen.add(key);
          territories.push({ city, state: state || null });
        }
        // Sort alphabetically by city for predictable display
        territories.sort((a, b) => a.city.localeCompare(b.city));
        setAgentTerritories(territories);
      } else {
        setAgentTerritories([]);
      }
    } catch (err) {
      console.error('Error fetching agent territories:', err);
      setTerritoriesError('Failed to load your territories.');
      setAgentTerritories([]);
    } finally {
      setIsLoadingTerritories(false);
    }
  };

  const fetchExistingMatches = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/curated-matches`, {
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();

      if (data.success) {
        const userMatches = data.data.filter(m => m.user._id === userId);
        setTotalMatchesSent(userMatches.length);

        const now = new Date();
        const currentMatches = userMatches.filter(m => {
          const expiresAt = new Date(m.expiresAt);
          return m.status === 'active' && now < expiresAt;
        });
        const historicalMatches = userMatches.filter(m => {
          const expiresAt = new Date(m.expiresAt);
          return m.status !== 'active' || now >= expiresAt;
        });
        historicalMatches.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        setExistingMatches(currentMatches);
        setMatchHistory(historicalMatches);
      } else {
        setExistingMatches([]);
        setMatchHistory([]);
        setTotalMatchesSent(0);
      }
    } catch (err) {
      console.error('Error fetching existing matches:', err);
      setExistingMatches([]);
      setMatchHistory([]);
      setTotalMatchesSent(0);
    }
  };

  const handlePropertySearch = (term) => {
    setPropertySearchTerm(term);
    if (!term.trim()) {
      setFilteredProperties(properties);
    } else {
      const t = term.toLowerCase();
      setFilteredProperties(properties.filter(p =>
        p.address?.toLowerCase().includes(t) ||
        (typeof p.neighborhood === 'string' && p.neighborhood.toLowerCase().includes(t)) ||
        (p.neighborhood?.name && p.neighborhood.name.toLowerCase().includes(t))
      ));
    }
  };

  const togglePropertySelection = (property) => {
    const isSelected = selectedProperties.find(p => p._id === property._id);
    if (isSelected) {
      setSelectedProperties(selectedProperties.filter(p => p._id !== property._id));
    } else {
      if (selectedProperties.length >= 6) {
        onError('Maximum 6 properties allowed per curated match');
        return;
      }
      setSelectedProperties([...selectedProperties, property]);
    }
  };

  const handleSendCuratedMatch = async () => {
    if (!buyer || selectedProperties.length === 0) {
      onError('Please select at least one property');
      return;
    }
    setIsCurating(true);
    try {
      const response = await fetch(`${API_URL}/curated-matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify({
          userId: buyer._id,
          propertyIds: selectedProperties.map(p => p._id)
        })
      });
      const data = await response.json();
      if (data.success) {
        onSuccess(`✅ Successfully sent ${selectedProperties.length} curated ${selectedProperties.length === 1 ? 'match' : 'matches'} to ${buyer.name}!`);
        setSelectedProperties([]);
        await fetchExistingMatches(buyer._id);
        setCurateView('current');
      } else {
        onError(data.error || 'Failed to send curated matches');
      }
    } catch (err) {
      onError('Error sending curated matches: ' + err.message);
    } finally {
      setIsCurating(false);
    }
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Are you sure you want to delete this curated match?')) return;
    try {
      const response = await fetch(`${API_URL}/curated-matches/${matchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
      });
      const data = await response.json();
      if (data.success) {
        onSuccess('Curated match deleted successfully');
        await fetchExistingMatches(buyer._id);
      } else {
        onError(data.error || 'Failed to delete curated match');
      }
    } catch (err) {
      console.error('Error deleting curated match:', err);
      onError('Error deleting curated match: ' + err.message);
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  // SEARCH MODE helpers
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Build initial searchForm values from the buyer's filterPreferences.
   * Falls back to sensible empties when prefs are missing.
   */
  function buildInitialSearchForm(b) {
    const fp = b?.filterPreferences || {};
    const pr = fp.priceRange || {};
    const hf = fp.homeFeatures || {};
    const af = fp.advancedFilters || {};
    const loc = fp.location || {};

    const ANY_MAX = 999999999;
    return {
      listingType: fp.listingType || 'purchase',
      minPrice: pr.min ? String(pr.min) : '',
      maxPrice: (pr.max && pr.max < ANY_MAX) ? String(pr.max) : '',
      minBedrooms: fp.bedrooms ? String(fp.bedrooms) : '',
      minBathrooms: fp.bathrooms ? String(fp.bathrooms) : '',
      propertyType: (fp.propertyTypes && fp.propertyTypes[0]) || 'any', // single-select for now
      // Location (only sent if customLocation present + has coords)
      latitude: loc.customLocationData?.coordinates?.latitude || '',
      longitude: loc.customLocationData?.coordinates?.longitude || '',
      radius: loc.radius ? String(loc.radius) : '25',
      // Home features
      minSqft: hf.minSqft ? String(hf.minSqft) : '',
      maxSqft: hf.maxSqft ? String(hf.maxSqft) : '',
      yearBuilt: hf.yearBuilt ? String(hf.yearBuilt) : '',
      garage: hf.garage ? String(hf.garage) : '',
      singleStory: !!hf.singleStory,
      // Advanced
      hoaPreference: af.hoaPreference || 'any',
      poolPreference: af.poolPreference || 'any',
      basementPreference: af.basementPreference || 'any',
      fireplacePreference: af.fireplacePreference || 'any',
      minSchoolRating: af.minSchoolRating ? String(af.minSchoolRating) : '',
      minLotSize: af.minLotSize ? String(af.minLotSize / 43560) : '', // controller takes acres
      maxLotSize: af.maxLotSize ? String(af.maxLotSize / 43560) : '',
      // Free-text address/description search
      search: '',
      // Selected cities (from the checkbox UI; empty = no city filter)
      cities: [],
      // Map area picker — null = no polygon constraint. When set, the search
      // uses POST /saved-areas/search with this geometry, ignoring the cities
      // list entirely (polygon takes precedence as the location filter).
      areaPolygon: null,
      areaSavedId: null,
    };
  }

  /**
   * Convert the searchForm into a query string for GET /api/properties.
   * Skips empty/'any' values so we don't over-constrain.
   */
  function searchFormToQueryString(form, page = 1, limit = 50) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));

    const passThrough = [
      'listingType', 'minPrice', 'maxPrice', 'minBedrooms', 'minBathrooms',
      'minSqft', 'maxSqft', 'yearBuilt', 'garage',
      'minSchoolRating', 'minLotSize', 'maxLotSize',
      'latitude', 'longitude', 'radius',
      'search',
    ];
    passThrough.forEach((k) => {
      const v = form[k];
      if (v !== '' && v != null) params.set(k, v);
    });

    // 'any' values mean "don't filter" — drop them
    ['propertyType', 'hoaPreference', 'poolPreference', 'basementPreference', 'fireplacePreference'].forEach((k) => {
      const v = form[k];
      if (v && v !== 'any') params.set(k, v);
    });

    if (form.singleStory) params.set('singleStory', 'true');

    // Cities filter.
    // - If the agent picked specific cities, send those.
    // - If they didn't pick any, default to ALL of their claimed territories.
    //   (Previously we fell back to the buyer's lat/lng/radius — which was useless
    //   when the buyer lived in a different metro than the agent's territory.)
    // Either way, we DO NOT send the buyer's coordinates: the city list is the only
    // geo restriction. If the agent has zero territories yet, send no geo filter at
    // all so they at least see something (and the empty-state hint will guide them).
    const explicitCities = Array.isArray(form.cities) ? form.cities : [];
    const effectiveCities = explicitCities.length > 0
      ? explicitCities
      : availableCities; // all agent territories
    if (effectiveCities.length > 0) {
      params.set('cities', effectiveCities.join(','));
    }
    params.delete('latitude');
    params.delete('longitude');
    params.delete('radius');

    return params.toString();
  }

  // Display a numeric string with thousands separators (e.g. "1250000" → "1,250,000").
  // Empty / non-numeric input returns ''.
  const formatNumberInput = (value) => {
    if (value == null || value === '') return '';
    const digits = String(value).replace(/[^\d]/g, '');
    if (!digits) return '';
    return Number(digits).toLocaleString('en-US');
  };

  // Strip everything but digits — what we actually store/send to the backend.
  const parseNumberInput = (value) => {
    if (value == null || value === '') return '';
    return String(value).replace(/[^\d]/g, '');
  };

  // Try to run a search, but if the MLS disclaimer hasn't been acknowledged, flash the
  // disclaimer boxes briefly so the agent knows what's blocking them.
  const tryFetchSearch = (page = 1, append = false) => {
    if (!mlsAcknowledged) {
      setMlsHighlighted(true);
      // Auto-clear the highlight after the animation
      setTimeout(() => setMlsHighlighted(false), 1600);
      return;
    }
    fetchSearchResults(page, append);
  };

  // `formOverride` lets the caller pass an explicit searchForm rather than
  // reading from React state. Necessary when triggering a fetch immediately
  // after a setSearchForm() call — the state update hasn't committed yet, so
  // reading from the closure would see the old form. The map picker uses
  // this on apply.
  const fetchSearchResults = async (page = 1, append = false, formOverride = null) => {
    const form = formOverride || searchForm;
    if (!form) return;
    setIsSearching(true);
    setHasSearchedYet(true);
    try {
      let response;

      if (form.areaPolygon) {
        // Polygon search — POST to /saved-areas/search with the geometry +
        // the same filters the GET path uses. The backend helper accepts
        // identical filter field names, so we can hand it the searchForm
        // largely unchanged. The polygon endpoint does not currently paginate;
        // it returns up to 500 results in one response. We map that into the
        // existing pagination shape so downstream UI doesn't need to change.
        const filters = { ...form };
        delete filters.areaPolygon;
        delete filters.areaSavedId;
        // Strip empty / 'any' values so they don't accidentally narrow results.
        Object.keys(filters).forEach(k => {
          if (filters[k] === '' || filters[k] == null) delete filters[k];
          if (filters[k] === 'any') delete filters[k];
        });
        // cities[] is optional — only send if explicitly chosen. Polygon already
        // restricts location, so cities would be a redundant intersection.
        if (Array.isArray(filters.cities) && filters.cities.length === 0) {
          delete filters.cities;
        }

        response = await fetch(`${API_URL}/saved-areas/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${AuthService.getToken()}`
          },
          body: JSON.stringify({
            geometry: form.areaPolygon,
            savedAreaId: form.areaSavedId || undefined,
            filters,
            limit: 500
          })
        });
        if (!response.ok) throw new Error(`API returned ${response.status}`);
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          // Polygon search is single-page; ignore append, replace results.
          setSearchResults(data.data);
          setSearchPage(1);
          setSearchTotalPages(1);
          setSearchTotal(data.count || data.data.length);
        } else {
          setSearchResults([]);
          setSearchTotal(0);
          setSearchTotalPages(0);
        }
      } else {
        // Standard city/filter search via GET /api/properties.
        const qs = searchFormToQueryString(form, page, 50);
        response = await fetch(`${API_URL}/properties?${qs}`, {
          headers: { 'Authorization': `Bearer ${AuthService.getToken()}` }
        });
        if (!response.ok) throw new Error(`API returned ${response.status}`);
        const data = await response.json();
        if (data.success && Array.isArray(data.data)) {
          if (append) {
            setSearchResults(prev => [...prev, ...data.data]);
          } else {
            setSearchResults(data.data);
          }
          setSearchPage(data.page || page);
          setSearchTotalPages(data.pages || 0);
          setSearchTotal(data.total || 0);
        } else if (!append) {
          setSearchResults([]);
          setSearchTotal(0);
          setSearchTotalPages(0);
        }
      }
    } catch (err) {
      console.error('Error searching properties:', err);
      onError('Failed to search properties: ' + err.message);
    } finally {
      setIsSearching(false);
    }

    // After a fresh search (not Load More), scroll the results into view.
    // Done after isSearching flips back, on a microtask delay so the empty/results
    // state has rendered before we measure.
    if (!append) {
      setTimeout(() => {
        resultsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  const loadMoreSearchResults = () => {
    if (searchPage < searchTotalPages) {
      fetchSearchResults(searchPage + 1, true);
    }
  };

  // ── Address lookup ──────────────────────────────────────────────────────────

  /**
   * The endpoint always returns 200 with success:true — an empty result is a
   * normal outcome, and `reason` says why. Map every code: a bare "no results"
   * makes a fixable input mistake look like a dead end.
   */
  const lookupMessage = (data) => {
    switch (data.reason) {
      case 'address_unusable':
        return { tone: 'hint', text: 'Enter a street number and name — e.g. "4521 Vine St".' };
      case 'area_unresolved':
        // Inventory was already checked and missed; a city/state is needed to go further.
        return { tone: 'hint', text: 'Add a city and state, or a ZIP — "4521 Vine St, Denver, CO".' };
      case 'state_not_enabled':
        return { tone: 'info', text: `Not in our listings, and we can't pull live MLS in ${data.state || 'that state'} yet.` };
      case 'user_quota_exceeded':
        return { tone: 'info', text: "You've hit today's live lookup limit. Filter search still works." };
      case 'not_found':
        return { tone: 'info', text: 'No active listing at that address. Check the spelling, or it may be off-market.' };
      case 'not_active':
        // MLSDetail resolves the most recent listing regardless of status, so
        // we can tell the agent WHY rather than just "nothing found".
        return { tone: 'info', text: `Found that address, but the listing is ${String(data.mlsStatus || 'not active').toLowerCase()} — nothing to curate.` };
      case 'found_but_not_ingestable':
        return { tone: 'info', text: "Found the listing, but it couldn't be imported. Try the MLS directly." };
      case 'mls_error':
        return { tone: 'error', text: 'MLS lookup failed. Try again in a moment.' };
      default:
        return { tone: 'info', text: 'No match for that address.' };
    }
  };

  /**
   * AutoComplete is unmetered on all RealEstateAPI plan tiers, which is the only
   * reason this is safe to fire per-keystroke. Do NOT copy this debounce onto
   * runAddressLookup — that one costs a real MLS call per miss and draws down a
   * 40/day per-agent quota.
   */
  const onAddressChange = (value) => {
    setAddressQuery(value);
    setSelectedSuggestion(null);   // typing invalidates a previous pick
    setHighlightedSuggestion(-1);
    setLookupNotice(null);

    clearTimeout(suggestDebounceRef.current);

    if (value.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    const seq = ++suggestSeqRef.current;

    suggestDebounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/mls/autocomplete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${AuthService.getToken()}`
          },
          body: JSON.stringify({ search: value.trim() })
        });
        const data = await response.json();
        // A slower earlier request must not overwrite a newer one's results.
        if (seq !== suggestSeqRef.current) return;
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
      } catch (err) {
        // Fail soft — autocomplete being down must never block manual entry.
        if (seq === suggestSeqRef.current) setSuggestions([]);
      }
    }, 250);
  };

  const pickSuggestion = (s) => {
    setAddressQuery(s.title || s.address || '');
    setSelectedSuggestion(s);
    setSuggestions([]);
    setHighlightedSuggestion(-1);
  };

  const clearAddressLookup = () => {
    clearTimeout(suggestDebounceRef.current);
    suggestSeqRef.current++;          // invalidate any in-flight suggestion fetch
    setAddressQuery('');
    setSuggestions([]);
    setSelectedSuggestion(null);
    setHighlightedSuggestion(-1);
    setLookupNotice(null);
    setLookupSource(null);
    setSearchResults([]);
    setSearchTotal(0);
    setSearchTotalPages(0);
    setHasSearchedYet(false);
  };

  const runAddressLookup = async () => {
    const q = addressQuery.trim();
    if (!q) return;

    // Same MLS-acknowledgement gate as the Search button. An inventory hit can
    // be stale, so the disclaimer applies to this path too.
    if (!mlsAcknowledged) {
      setMlsHighlighted(true);
      setTimeout(() => setMlsHighlighted(false), 1600);
      return;
    }

    clearTimeout(suggestDebounceRef.current);
    suggestSeqRef.current++;
    setSuggestions([]);
    setIsLookingUp(true);
    setLookupNotice(null);
    setLookupSource(null);

    // When a suggestion was picked we have real, already-split city/state/zip —
    // far better than making the backend guess them out of a comma split.
    const body = selectedSuggestion
      ? {
          address: selectedSuggestion.address,
          city: selectedSuggestion.city,
          state: selectedSuggestion.state,
          zip: selectedSuggestion.zip
        }
      : { address: q };

    try {
      const response = await fetch(`${API_URL}/mls/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getToken()}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error(`API returned ${response.status}`);
      const data = await response.json();

      if (data.count > 0 && Array.isArray(data.properties)) {
        // Reuse the existing results grid — same property shape, same cards,
        // same select-to-curate. Nothing downstream needs to change.
        setSearchResults(data.properties);
        setSearchTotal(data.count);
        setSearchPage(1);
        setSearchTotalPages(1);   // hides Load More via the existing page < pages guard
        setHasSearchedYet(true);
        setLookupSource(data.source || null);
      } else {
        setSearchResults([]);
        setSearchTotal(0);
        setSearchTotalPages(0);
        setHasSearchedYet(true);
        setLookupNotice(lookupMessage(data));
      }
    } catch (err) {
      console.error('Address lookup failed:', err);
      setLookupNotice({ tone: 'error', text: 'Lookup failed. Try again in a moment.' });
    } finally {
      setIsLookingUp(false);
      setTimeout(() => {
        resultsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  };

  const onAddressKeyDown = (e) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedSuggestion(i => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedSuggestion(i => (i <= 0 ? suggestions.length - 1 : i - 1));
        return;
      }
      if (e.key === 'Escape') {
        setSuggestions([]);
        setHighlightedSuggestion(-1);
        return;
      }
      if (e.key === 'Enter' && highlightedSuggestion >= 0) {
        e.preventDefault();
        pickSuggestion(suggestions[highlightedSuggestion]);
        return;
      }
    }
    // Submit on Enter — never debounce the lookup itself, see onAddressChange.
    if (e.key === 'Enter') {
      e.preventDefault();
      runAddressLookup();
    }
  };

  const updateSearchField = (key, value) => {
    setSearchForm(prev => ({ ...(prev || {}), [key]: value }));
  };

  const toggleCity = (city) => {
    setSearchForm(prev => {
      const current = (prev && Array.isArray(prev.cities)) ? prev.cities : [];
      const next = current.includes(city)
        ? current.filter(c => c !== city)
        : [...current, city];
      return { ...(prev || {}), cities: next };
    });
  };

  // Cities to show in the dropdown — driven by the agent's claimed AgentPool territories.
  // Each territory is { city, state }. The checkbox VALUE is the city name (which is what
  // the backend's `cities=` param matches against the property's csz field).
  const availableCities = useMemo(() => {
    return agentTerritories.map(t => t.city);
  }, [agentTerritories]);

  // Type-ahead filtered list inside the dropdown
  const filteredAvailableCities = useMemo(() => {
    const q = cityFilterText.trim().toLowerCase();
    if (!q) return availableCities;
    return availableCities.filter(c => c.toLowerCase().includes(q));
  }, [availableCities, cityFilterText]);

  // Whether the current searchForm matches what the user's saved preferences would produce.
  // Used to flip the prefill banner between "Clear all" (when prefs are applied) and
  // "Reset to user's preferences" (when the agent has cleared/edited them).
  // We intentionally ignore `search` and `cities` — those aren't part of the user's prefs.
  const userPrefsForm = useMemo(() => buildInitialSearchForm(buyer), [buyer]);
  const formMatchesUserPrefs = useMemo(() => {
    if (!searchForm) return false;
    const fields = [
      'listingType', 'minPrice', 'maxPrice', 'minBedrooms', 'minBathrooms',
      'propertyType', 'minSqft', 'maxSqft', 'yearBuilt', 'garage', 'singleStory',
      'hoaPreference', 'poolPreference', 'basementPreference', 'fireplacePreference',
      'minSchoolRating', 'minLotSize', 'maxLotSize',
    ];
    return fields.every(k => searchForm[k] === userPrefsForm[k]);
  }, [searchForm, userPrefsForm]);

  // Build Sets of liked/disliked property IDs for fast O(1) badge lookup
  const likedIds = useMemo(() => {
    const arr = (buyer?.likes || []).map(l => (typeof l === 'string' ? l : l?._id?.toString?.() || l?._id));
    return new Set(arr.filter(Boolean));
  }, [buyer?.likes]);
  const dislikedIds = useMemo(() => {
    const arr = (buyer?.dislikes || []).map(d => (typeof d === 'string' ? d : d?._id?.toString?.() || d?._id));
    return new Set(arr.filter(Boolean));
  }, [buyer?.dislikes]);

  // What the grid actually renders depends on which sub-mode the agent picked
  const isSearchMode = curateView === 'create' && createMode === 'search';
  const displayedProperties = isSearchMode ? searchResults : filteredProperties;
  const displayedTotal = isSearchMode ? searchTotal : propertiesTotal;
  const displayedPage = isSearchMode ? searchPage : propertiesPage;
  const displayedTotalPages = isSearchMode ? searchTotalPages : propertiesTotalPages;
  const isDisplayedLoading = isSearchMode ? isSearching : isLoadingMoreProperties;

  if (!isOpen || !portalTarget) return null;

  return ReactDOM.createPortal((
    <div
      className="modal-overlay curate-matches-modal light-mode"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
          <style>{`
            .curate-matches-modal {
              position: fixed;
              top: 0 !important;
              left: 0 !important;
              right: 0 !important;
              bottom: 0 !important;
              padding: 0 !important;
              margin: 0 !important;
              background: rgba(0, 0, 0, 0.85);
              backdrop-filter: blur(8px);
              display: flex;
              align-items: stretch;
              justify-content: stretch;
              z-index: 999999;
              animation: fadeIn 0.2s ease-out;
            }
            
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            
            @keyframes slideUp {
              from { opacity: 0; transform: translateY(20px) scale(0.98); }
              to { opacity: 1; transform: translateY(0) scale(1); }
            }
            
            .curate-modal-container {
              width: 100vw;
              height: 100vh;
              max-width: 100vw;
              max-height: 100vh;
              background: linear-gradient(145deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.99) 100%);
              border-radius: 0;
              border: none;
              box-shadow: none;
              overflow: hidden;
              display: flex;
              flex-direction: column;
              animation: slideUp 0.3s ease-out;
            }
            
            .curate-modal-header {
              background: #ffffff;
              padding: 28px 32px;
              border-bottom: 1px solid rgba(0, 0, 0, 0.04);
              position: relative;
              overflow: hidden;
            }
            
            .curate-modal-header::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: radial-gradient(ellipse at top right, rgba(0, 0, 0, 0.02), transparent 60%);
              pointer-events: none;
            }
            
            .curate-header-content {
              display: flex;
              justify-content: space-between;
              align-items: center;
              position: relative;
              z-index: 1;
            }
            
            .curate-header-left {
              display: flex;
              align-items: center;
            }
            
            .curate-header-info {
              text-align: left;
            }
            
            .curate-header-info h2 {
              margin: 0 0 2px 0;
              font-size: 0.9rem;
              font-weight: 500;
              color: #9ca3af;
              letter-spacing: 0;
              text-transform: uppercase;
            }
            
            .curate-user-name {
              font-size: 1.4rem;
              font-weight: 700;
              color: #ffffff;
              letter-spacing: -0.02em;
            }
            
            .curate-header-right {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            
            .curate-stat-pill {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 6px 12px;
              background: rgba(0, 0, 0, 0.04);
              border-radius: 20px;
              font-size: 0.8rem;
              color: #374151;
            }
            
            .curate-stat-pill svg {
              opacity: 0.7;
            }
            
            .curate-close-btn {
              width: 40px;
              height: 40px;
              border-radius: 12px;
              border: 1px solid rgba(0, 0, 0, 0.06);
              background: rgba(0, 0, 0, 0.03);
              color: #6b7280;
              font-size: 1.25rem;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s;
            }
            
            .curate-close-btn:hover {
              background: rgba(0, 0, 0, 0.06);
              border-color: rgba(0, 0, 0, 0.1);
              color: #fff;
              transform: scale(1.05);
            }
            
            .curate-tabs-container {
              padding: 20px 32px 0;
              background: rgba(0, 0, 0, 0.2);
            }
            
            .curate-tabs {
              display: flex;
              gap: 8px;
              position: relative;
            }
            
            .curate-tab {
              padding: 14px 24px;
              border-radius: 12px 12px 0 0;
              border: none;
              background: transparent;
              color: #9ca3af;
              font-size: 0.9rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              gap: 10px;
              position: relative;
            }
            
            .curate-tab:hover:not(.active) {
              color: #374151;
              background: rgba(0, 0, 0, 0.02);
            }
            
            .curate-tab.active {
              background: rgba(30, 30, 35, 0.98);
              color: #fff;
            }
            
            .curate-tab.active::after {
              content: '';
              position: absolute;
              bottom: 0;
              left: 50%;
              transform: translateX(-50%);
              width: 40px;
              height: 3px;
              background: #374151;
              border-radius: 3px 3px 0 0;
            }
            
            .curate-tab-badge {
              padding: 2px 8px;
              border-radius: 10px;
              font-size: 0.75rem;
              font-weight: 700;
              background: rgba(0, 0, 0, 0.06);
              color: #6b7280;
            }
            
            .curate-tab.active .curate-tab-badge {
              background: rgba(0, 0, 0, 0.1);
              color: white;
            }
            
            .curate-modal-body {
              flex: 1;
              overflow-y: auto;
              padding: 28px 32px;
              background: rgba(30, 30, 35, 0.98);
            }
            
            .curate-modal-body::-webkit-scrollbar {
              width: 6px;
            }
            
            .curate-modal-body::-webkit-scrollbar-track {
              background: rgba(0, 0, 0, 0.02);
            }
            
            .curate-modal-body::-webkit-scrollbar-thumb {
              background: rgba(0, 0, 0, 0.06);
              border-radius: 3px;
            }
            
            .curate-modal-body::-webkit-scrollbar-thumb:hover {
              background: rgba(0, 0, 0, 0.08);
            }
            
            .curate-empty-state {
              text-align: center;
              padding: 60px 40px;
            }
            
            .curate-empty-icon {
              width: 80px;
              height: 80px;
              margin: 0 auto 24px;
              background: rgba(0, 0, 0, 0.03);
              border-radius: 24px;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 1px solid rgba(0, 0, 0, 0.04);
            }
            
            .curate-empty-title {
              font-size: 1.25rem;
              font-weight: 600;
              color: #1a1a1a;
              margin-bottom: 8px;
            }
            
            .curate-empty-subtitle {
              color: #9ca3af;
              font-size: 0.95rem;
              max-width: 400px;
              margin: 0 auto;
              line-height: 1.6;
            }
            
            .curate-match-card {
              background: linear-gradient(145deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.01) 100%);
              border: 1px solid rgba(0, 0, 0, 0.04);
              border-radius: 16px;
              padding: 24px;
              margin-bottom: 16px;
              transition: all 0.2s;
            }
            
            .curate-match-card:hover {
              border-color: rgba(0, 0, 0, 0.08);
              background: linear-gradient(145deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.02) 100%);
            }
            
            .curate-match-card.history {
              opacity: 0.8;
            }
            
            .curate-match-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 20px;
            }
            
            .curate-match-stats {
              display: flex;
              gap: 12px;
              align-items: center;
              flex-wrap: wrap;
            }
            
            .curate-status-badge {
              padding: 6px 14px;
              border-radius: 20px;
              font-size: 0.8rem;
              font-weight: 600;
              display: inline-flex;
              align-items: center;
              gap: 6px;
            }
            
            .curate-status-badge.active {
              background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%);
              color: #4ade80;
              border: 1px solid rgba(34, 197, 94, 0.2);
            }
            
            .curate-status-badge.expired {
              background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(239, 68, 68, 0.05) 100%);
              color: #fca5a5;
              border: 1px solid rgba(239, 68, 68, 0.2);
            }
            
            .curate-status-badge.completed {
              background: rgba(0, 0, 0, 0.04);
              color: #6b7280;
              border: 1px solid rgba(0, 0, 0, 0.08);
            }
            
            .curate-match-meta {
              display: flex;
              align-items: center;
              gap: 8px;
              color: #9ca3af;
              font-size: 0.85rem;
            }
            
            .curate-match-meta svg {
              opacity: 0.6;
            }
            
            .curate-delete-btn {
              padding: 8px 16px;
              border-radius: 10px;
              border: 1px solid rgba(239, 68, 68, 0.2);
              background: rgba(239, 68, 68, 0.08);
              color: #fca5a5;
              font-size: 0.85rem;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            
            .curate-delete-btn:hover {
              background: rgba(239, 68, 68, 0.15);
              border-color: rgba(239, 68, 68, 0.3);
            }
            
            .curate-properties-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
              gap: 12px;
            }
            
            .curate-property-mini {
              background: rgba(0, 0, 0, 0.2);
              border: 1px solid rgba(0, 0, 0, 0.03);
              border-radius: 12px;
              overflow: hidden;
              transition: all 0.2s;
            }
            
            .curate-property-mini:hover {
              border-color: rgba(0, 0, 0, 0.06);
              transform: translateY(-2px);
            }
            
            .curate-property-mini img {
              width: 100%;
              height: 90px;
              object-fit: cover;
            }
            
            .curate-property-mini-info {
              padding: 10px;
            }
            
            .curate-property-mini-address {
              font-size: 0.8rem;
              font-weight: 500;
              color: #1a1a1a;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-bottom: 4px;
            }
            
            .curate-property-mini-price {
              font-size: 0.75rem;
              color: #4ade80;
              font-weight: 600;
            }
            
            .curate-search-section {
              margin-bottom: 24px;
            }
            
            .curate-search-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 12px;
            }
            
            .curate-search-label {
              font-size: 0.9rem;
              font-weight: 500;
              color: #6b7280;
            }
            
            .curate-refresh-btn {
              padding: 8px 16px;
              border-radius: 10px;
              border: 1px solid rgba(0, 0, 0, 0.08);
              background: rgba(0, 0, 0, 0.04);
              color: #374151;
              font-size: 0.85rem;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            
            .curate-refresh-btn:hover:not(:disabled) {
              background: rgba(0, 0, 0, 0.08);
              border-color: #d1d5db;
            }
            
            .curate-refresh-btn:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
            
            .curate-search-input {
              width: 100%;
              padding: 14px 18px;
              border-radius: 12px;
              border: 1px solid rgba(0, 0, 0, 0.04);
              background: rgba(0, 0, 0, 0.3);
              color: #fff;
              font-size: 0.95rem;
              transition: all 0.2s;
            }
            
            .curate-search-input:focus {
              outline: none;
              border-color: #d1d5db;
              background: rgba(0, 0, 0, 0.4);
              box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.03);
            }
            
            .curate-search-input::placeholder {
              color: #d1d5db;
            }

            /* ─── Mode toggle (Algorithm vs Search) ─── */
            .curate-mode-toggle {
              display: inline-flex;
              gap: 4px;
              padding: 4px;
              background: rgba(0, 0, 0, 0.04);
              border: 1px solid rgba(0, 0, 0, 0.06);
              border-radius: 999px;
              margin-bottom: 16px;
            }
            .curate-mode-pill {
              display: inline-flex;
              align-items: center;
              padding: 8px 18px;
              border: none;
              background: transparent;
              border-radius: 999px;
              font-size: 0.85rem;
              font-weight: 600;
              color: #6b7280;
              cursor: pointer;
              transition: all 0.15s ease;
            }
            .curate-mode-pill:hover { color: #1f2937; }
            .curate-mode-pill.active {
              background: #1f2937;
              color: #ffffff;
              box-shadow: 0 2px 8px rgba(31, 41, 55, 0.25);
            }

            /* ─── Search form ─── */
            .curate-search-form {
              background: rgba(0, 0, 0, 0.02);
              border: 1px solid rgba(0, 0, 0, 0.06);
              border-radius: 14px;
              padding: 16px;
              margin-bottom: 20px;
            }
            .curate-addr-lookup {
              position: relative;
              margin-bottom: 14px;
              padding: 13px 15px;
              background: rgba(99, 102, 241, 0.04);
              border: 1px solid rgba(99, 102, 241, 0.18);
              border-radius: 12px;
            }
            .curate-addr-lookup-head {
              display: flex;
              align-items: baseline;
              flex-wrap: wrap;
              gap: 8px;
              margin-bottom: 9px;
            }
            .curate-addr-lookup-title {
              font-size: 0.8rem;
              font-weight: 600;
              color: #111;
            }
            .curate-addr-lookup-sub {
              font-size: 0.72rem;
              color: rgba(0, 0, 0, 0.5);
            }
            .curate-addr-lookup-row {
              display: flex;
              align-items: center;
              gap: 8px;
            }
            .curate-addr-input-wrap {
              position: relative;
              flex: 1;
              min-width: 0;
            }
            .curate-addr-input {
              width: 100%;
              padding: 9px 12px;
              font-size: 0.85rem;
              color: #111;
              background: #fff;
              border: 1px solid rgba(0, 0, 0, 0.14);
              border-radius: 8px;
              outline: none;
              transition: border-color 0.15s, box-shadow 0.15s;
            }
            .curate-addr-input:focus {
              border-color: rgba(99, 102, 241, 0.55);
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
            }
            .curate-addr-suggestions {
              position: absolute;
              top: calc(100% + 4px);
              left: 0;
              right: 0;
              z-index: 20;
              background: #fff;
              border: 1px solid rgba(0, 0, 0, 0.12);
              border-radius: 10px;
              box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
              overflow: hidden;
            }
            .curate-addr-suggestion {
              display: flex;
              align-items: center;
              gap: 8px;
              width: 100%;
              padding: 9px 12px;
              font-size: 0.82rem;
              text-align: left;
              color: #111;
              background: transparent;
              border: none;
              border-bottom: 1px solid rgba(0, 0, 0, 0.05);
              cursor: pointer;
            }
            .curate-addr-suggestion.highlighted {
              background: rgba(99, 102, 241, 0.08);
            }
            .curate-addr-suggestion-icon {
              flex-shrink: 0;
              color: rgba(0, 0, 0, 0.35);
            }
            .curate-addr-suggestions-note {
              padding: 7px 12px;
              font-size: 0.68rem;
              color: rgba(0, 0, 0, 0.45);
              background: rgba(0, 0, 0, 0.02);
            }
            .curate-addr-find-btn {
              display: inline-flex;
              align-items: center;
              gap: 5px;
              flex-shrink: 0;
              padding: 9px 16px;
              font-size: 0.82rem;
              font-weight: 600;
              color: #fff;
              background: #6366f1;
              border: none;
              border-radius: 8px;
              cursor: pointer;
              transition: background-color 0.15s, opacity 0.15s;
            }
            .curate-addr-find-btn:hover:not(:disabled) {
              background: #4f46e5;
            }
            .curate-addr-find-btn:disabled {
              opacity: 0.45;
              cursor: not-allowed;
            }
            .curate-addr-find-btn.gated {
              background: rgba(0, 0, 0, 0.28);
            }
            .curate-addr-clear-btn {
              flex-shrink: 0;
              padding: 9px 12px;
              font-size: 0.78rem;
              font-weight: 500;
              color: rgba(0, 0, 0, 0.6);
              background: transparent;
              border: 1px solid rgba(0, 0, 0, 0.14);
              border-radius: 8px;
              cursor: pointer;
            }
            .curate-addr-clear-btn:hover {
              color: #111;
              background: rgba(0, 0, 0, 0.03);
            }
            .curate-addr-notice {
              margin-top: 9px;
              padding: 8px 11px;
              font-size: 0.76rem;
              border-radius: 7px;
              line-height: 1.4;
            }
            .curate-addr-notice.hint {
              color: #92600a;
              background: #fef6e7;
              border: 1px solid #f5d98b;
            }
            .curate-addr-notice.info {
              color: rgba(0, 0, 0, 0.62);
              background: rgba(0, 0, 0, 0.03);
              border: 1px solid rgba(0, 0, 0, 0.08);
            }
            .curate-addr-notice.error {
              color: #9b2c2c;
              background: #fdeceb;
              border: 1px solid #f3c3c0;
            }
            .curate-addr-notice.source {
              color: #3730a3;
              background: rgba(99, 102, 241, 0.07);
              border: 1px solid rgba(99, 102, 241, 0.2);
            }
            .curate-search-form-row {
              display: flex;
              gap: 8px;
              margin-bottom: 12px;
            }
            .curate-search-form-row .curate-search-input { flex: 1; margin: 0; }
            .curate-search-go-btn {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 10px 18px;
              background: #1f2937;
              color: #ffffff;
              border: none;
              border-radius: 10px;
              font-size: 0.85rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.15s ease;
              white-space: nowrap;
            }
            .curate-search-go-btn:hover:not(:disabled) {
              background: #111827;
              box-shadow: 0 4px 14px rgba(31, 41, 55, 0.3);
            }
            .curate-search-go-btn:disabled { opacity: 0.5; cursor: not-allowed; }
            .curate-search-form-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
              gap: 12px;
              margin-bottom: 12px;
            }
            .curate-search-field {
              display: flex;
              flex-direction: column;
              gap: 4px;
            }
            .curate-search-field > span {
              font-size: 0.7rem;
              text-transform: uppercase;
              letter-spacing: 0.4px;
              color: rgba(0, 0, 0, 0.5);
              font-weight: 600;
            }
            .curate-search-field input,
            .curate-search-field select {
              padding: 9px 12px;
              border: 1px solid rgba(0, 0, 0, 0.1);
              border-radius: 8px;
              background: #ffffff;
              font-size: 0.85rem;
              color: #1f2937;
              transition: border-color 0.15s ease, box-shadow 0.15s ease;
            }
            .curate-search-field input:focus,
            .curate-search-field select:focus {
              outline: none;
              border-color: #1f2937;
              box-shadow: 0 0 0 3px rgba(31, 41, 55, 0.1);
            }
            .curate-search-field-checkbox {
              flex-direction: row;
              align-items: center;
              gap: 8px;
              padding-top: 16px;
            }
            .curate-search-field-checkbox > span {
              font-size: 0.85rem;
              text-transform: none;
              letter-spacing: 0;
              font-weight: 500;
              color: #1f2937;
            }

            /* ─── Prefill banner (top of search form) ─── */
            .curate-prefill-banner {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              flex-wrap: wrap;
              margin-bottom: 14px;
              padding: 10px 14px;
              background: rgba(99, 102, 241, 0.08);
              border: 1px solid rgba(99, 102, 241, 0.18);
              border-radius: 10px;
            }
            .curate-prefill-banner-text {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 0.82rem;
              color: rgba(0, 0, 0, 0.72);
              line-height: 1.4;
            }
            .curate-prefill-banner-text strong {
              color: #1f2937;
              font-weight: 600;
            }
            .curate-prefill-banner-icon {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 22px;
              height: 22px;
              flex-shrink: 0;
              border-radius: 50%;
              background: rgba(99, 102, 241, 0.18);
              color: #6366f1;
              font-size: 0.85rem;
              font-weight: 700;
            }
            .curate-prefill-banner-actions {
              display: flex;
              gap: 6px;
              flex-shrink: 0;
            }
            .curate-prefill-btn {
              padding: 6px 12px;
              background: #ffffff;
              border: 1px solid rgba(0, 0, 0, 0.1);
              border-radius: 7px;
              font-size: 0.75rem;
              font-weight: 600;
              color: #4b5563;
              cursor: pointer;
              transition: all 0.15s ease;
              white-space: nowrap;
            }
            .curate-prefill-btn:hover {
              background: rgba(0, 0, 0, 0.04);
              color: #1f2937;
              border-color: rgba(0, 0, 0, 0.2);
            }
            .curate-prefill-btn-danger {
              color: #b91c1c;
              border-color: rgba(185, 28, 28, 0.2);
            }
            .curate-prefill-btn-danger:hover {
              background: rgba(185, 28, 28, 0.06);
              color: #b91c1c;
              border-color: rgba(185, 28, 28, 0.4);
            }

            /* ─── MLS acknowledgement ─── */
            .curate-mls-disclaimer {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              margin: 24px auto;
              padding: 12px 16px;
              max-width: 620px;
              background: rgba(0, 0, 0, 0.025);
              border: 1px solid rgba(0, 0, 0, 0.07);
              border-radius: 10px;
              cursor: pointer;
              user-select: none;
            }
            .curate-mls-disclaimer input[type="checkbox"] {
              flex-shrink: 0;
              width: 16px;
              height: 16px;
              margin-top: 2px;
              accent-color: #1f2937;
              cursor: pointer;
            }
            .curate-mls-disclaimer > span {
              font-size: 0.75rem;
              line-height: 1.55;
              color: rgba(0, 0, 0, 0.62);
              font-style: italic;
            }
            /* Flash when agent tries to search without acknowledging */
            .curate-mls-disclaimer.highlighted {
              animation: curate-mls-flash 1.4s ease-out;
            }
            @keyframes curate-mls-flash {
              0%   { background: rgba(0, 0, 0, 0.025); border-color: rgba(0, 0, 0, 0.07); box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
              15%  { background: rgba(245, 158, 11, 0.15); border-color: rgba(245, 158, 11, 0.6); box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.18); }
              50%  { background: rgba(245, 158, 11, 0.12); border-color: rgba(245, 158, 11, 0.5); box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.12); }
              100% { background: rgba(0, 0, 0, 0.025); border-color: rgba(0, 0, 0, 0.07); box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
            }
            /* Subtle "gated" appearance on the Search button — visible but clearly not-yet-active */
            .curate-search-go-btn.gated {
              opacity: 0.55;
              cursor: pointer;
            }
            .curate-search-go-btn.gated:hover {
              opacity: 0.7;
              background: #1f2937;
            }

            /* ─── Cities multi-select dropdown ─── */
            .curate-cities-section {
              position: relative;
              margin-top: 12px;
              padding: 14px 16px;
              background: rgba(0, 0, 0, 0.02);
              border: 1px solid rgba(0, 0, 0, 0.06);
              border-radius: 12px;
            }
            .curate-loc-mode {
              display: inline-flex;
              align-items: center;
              gap: 0;
              padding: 3px;
              margin-bottom: 10px;
              background: rgba(0, 0, 0, 0.04);
              border: 1px solid rgba(0, 0, 0, 0.06);
              border-radius: 999px;
            }
            .curate-loc-mode-pill {
              display: inline-flex;
              align-items: center;
              padding: 6px 14px;
              font-size: 0.78rem;
              font-weight: 600;
              color: rgba(0, 0, 0, 0.55);
              background: transparent;
              border: none;
              border-radius: 999px;
              cursor: pointer;
              transition: background 0.15s, color 0.15s, box-shadow 0.15s;
            }
            .curate-loc-mode-pill:hover {
              color: rgba(0, 0, 0, 0.78);
            }
            .curate-loc-mode-pill.active {
              background: #ffffff;
              color: #111;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
            }
            .curate-loc-area-active {
              margin-top: 4px;
            }
            .curate-loc-area-pill {
              display: inline-flex;
              align-items: center;
              gap: 8px;
              padding: 9px 14px;
              background: #ffffff;
              border: 1px solid rgba(99, 102, 241, 0.25);
              border-radius: 8px;
              font-size: 0.82rem;
              color: #111;
              font-weight: 500;
              box-shadow: 0 1px 2px rgba(0,0,0,0.04);
            }
            .curate-loc-area-pill-label {
              font-weight: 600;
            }
            .curate-loc-area-edit {
              margin-left: 8px;
              padding: 3px 10px;
              font-size: 0.72rem;
              font-weight: 600;
              color: #6366f1;
              background: rgba(99, 102, 241, 0.08);
              border: 1px solid rgba(99, 102, 241, 0.25);
              border-radius: 6px;
              cursor: pointer;
              transition: background-color 0.15s;
            }
            .curate-loc-area-edit:hover {
              background: rgba(99, 102, 241, 0.14);
            }
            .curate-cities-label-row {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              margin-bottom: 8px;
            }
            .curate-cities-label {
              font-size: 0.7rem;
              text-transform: uppercase;
              letter-spacing: 0.4px;
              color: rgba(0, 0, 0, 0.5);
              font-weight: 600;
            }
            .curate-cities-action-btn {
              padding: 4px 10px;
              background: transparent;
              border: 1px solid rgba(0, 0, 0, 0.1);
              border-radius: 6px;
              font-size: 0.7rem;
              font-weight: 600;
              color: #6b7280;
              cursor: pointer;
              transition: all 0.15s ease;
            }
            .curate-cities-action-btn:hover:not(:disabled) {
              background: rgba(0, 0, 0, 0.04);
              color: #1f2937;
            }
            .curate-cities-action-btn:disabled {
              opacity: 0.35;
              cursor: not-allowed;
            }

            /* The dropdown trigger (looks like a select) */
            .curate-cities-trigger {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 8px;
              min-height: 42px;
              padding: 6px 12px;
              background: #ffffff;
              border: 1px solid rgba(0, 0, 0, 0.12);
              border-radius: 10px;
              cursor: pointer;
              transition: border-color 0.15s ease, box-shadow 0.15s ease;
            }
            .curate-cities-trigger:hover { border-color: rgba(0, 0, 0, 0.25); }
            .curate-cities-trigger.open {
              border-color: #1f2937;
              box-shadow: 0 0 0 3px rgba(31, 41, 55, 0.1);
            }
            .curate-cities-trigger-placeholder {
              color: rgba(0, 0, 0, 0.4);
              font-size: 0.85rem;
              flex: 1;
            }
            .curate-cities-trigger-tags {
              display: flex;
              flex-wrap: wrap;
              gap: 6px;
              flex: 1;
            }
            .curate-cities-tag {
              display: inline-flex;
              align-items: center;
              gap: 4px;
              padding: 3px 4px 3px 10px;
              background: #1f2937;
              color: #ffffff;
              border-radius: 999px;
              font-size: 0.78rem;
              font-weight: 500;
            }
            .curate-cities-tag-x {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 18px;
              height: 18px;
              padding: 0;
              border: none;
              background: rgba(255, 255, 255, 0.18);
              color: #ffffff;
              border-radius: 50%;
              cursor: pointer;
              font-size: 0.85rem;
              line-height: 1;
              transition: background 0.12s ease;
            }
            .curate-cities-tag-x:hover { background: rgba(255, 255, 255, 0.32); }
            .curate-cities-trigger-chev {
              flex-shrink: 0;
              color: rgba(0, 0, 0, 0.4);
            }

            /* The dropdown panel */
            .curate-cities-dropdown {
              position: absolute;
              top: calc(100% + 4px);
              left: 16px;
              right: 16px;
              z-index: 50;
              background: #ffffff;
              border: 1px solid rgba(0, 0, 0, 0.1);
              border-radius: 10px;
              box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
              overflow: hidden;
              animation: cmDropdownIn 0.12s ease-out;
            }
            @keyframes cmDropdownIn {
              from { opacity: 0; transform: translateY(-4px); }
              to { opacity: 1; transform: translateY(0); }
            }
            .curate-cities-dropdown-search {
              width: 100%;
              padding: 10px 14px;
              border: none;
              border-bottom: 1px solid rgba(0, 0, 0, 0.06);
              font-size: 0.9rem;
              color: #1f2937;
              outline: none;
              background: #ffffff;
            }
            .curate-cities-dropdown-search::placeholder { color: rgba(0, 0, 0, 0.35); }
            .curate-cities-dropdown-actions {
              display: flex;
              gap: 6px;
              padding: 8px 12px;
              border-bottom: 1px solid rgba(0, 0, 0, 0.06);
              background: rgba(0, 0, 0, 0.015);
            }
            .curate-cities-dropdown-list {
              max-height: 260px;
              overflow-y: auto;
              padding: 4px 0;
            }
            .curate-cities-dropdown-list::-webkit-scrollbar { width: 8px; }
            .curate-cities-dropdown-list::-webkit-scrollbar-thumb {
              background: rgba(0, 0, 0, 0.15);
              border-radius: 4px;
            }
            .curate-cities-dropdown-item {
              display: flex;
              align-items: center;
              gap: 10px;
              padding: 9px 14px;
              cursor: pointer;
              font-size: 0.9rem;
              color: #1f2937;
              transition: background-color 0.1s ease;
              user-select: none;
            }
            .curate-cities-dropdown-item:hover { background: rgba(0, 0, 0, 0.04); }
            .curate-cities-dropdown-item.checked {
              background: rgba(31, 41, 55, 0.06);
              font-weight: 600;
            }
            .curate-cities-dropdown-item input[type="checkbox"] {
              width: 16px;
              height: 16px;
              accent-color: #1f2937;
              cursor: pointer;
            }
            .curate-cities-dropdown-empty {
              padding: 14px;
              text-align: center;
              color: rgba(0, 0, 0, 0.4);
              font-size: 0.85rem;
              font-style: italic;
            }

            /* ─── Swipe-status badges on property cards ─── */
            .curate-swipe-badge {
              position: absolute;
              top: 8px;
              left: 8px;
              padding: 4px 10px;
              border-radius: 999px;
              font-size: 0.7rem;
              font-weight: 700;
              letter-spacing: 0.3px;
              z-index: 2;
              backdrop-filter: blur(6px);
              box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
            }
            .curate-swipe-badge-liked {
              background: rgba(239, 68, 68, 0.92);
              color: #ffffff;
            }
            .curate-swipe-badge-passed {
              background: rgba(75, 85, 99, 0.92);
              color: #ffffff;
            }

            .curate-selected-bar {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 16px 20px;
              background: rgba(0, 0, 0, 0.03);
              border: 1px solid rgba(0, 0, 0, 0.06);
              border-radius: 14px;
              margin-bottom: 20px;
            }
            
            .curate-selected-info {
              display: flex;
              align-items: center;
              gap: 12px;
            }
            
            .curate-selected-count {
              font-weight: 700;
              color: #fff;
              font-size: 1rem;
            }
            
            .curate-selected-label {
              color: #6b7280;
              font-size: 0.9rem;
            }
            
            .curate-selected-dots {
              display: flex;
              gap: 6px;
            }
            
            .curate-selected-dot {
              width: 10px;
              height: 10px;
              border-radius: 50%;
              background: rgba(0, 0, 0, 0.08);
              transition: all 0.2s;
            }
            
            .curate-selected-dot.filled {
              background: #374151;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }
            
            .curate-create-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 20px;
              max-height: 500px;
              overflow-y: auto;
              padding: 4px;
            }
            
            .curate-create-grid::-webkit-scrollbar {
              width: 6px;
            }
            
            .curate-create-grid::-webkit-scrollbar-track {
              background: rgba(0, 0, 0, 0.02);
            }
            
            .curate-create-grid::-webkit-scrollbar-thumb {
              background: rgba(0, 0, 0, 0.06);
              border-radius: 3px;
            }
            
            .curate-property-card {
              position: relative;
              background: rgba(0, 0, 0, 0.2);
              border: 2px solid rgba(0, 0, 0, 0.04);
              border-radius: 14px;
              overflow: hidden;
              cursor: pointer;
              transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            }
            
            .curate-property-card:hover {
              border-color: rgba(0, 0, 0, 0.1);
              transform: translateY(-4px);
              box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
            }
            
            .curate-property-card.selected {
              border-color: #9ca3af;
              box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06), 0 12px 30px rgba(0, 0, 0, 0.3);
            }
            
            .curate-property-card img {
              width: 100%;
              height: 180px;
              object-fit: cover;
              transition: transform 0.3s;
            }
            
            .curate-property-card:hover img {
              transform: scale(1.05);
            }
            
            .curate-property-card-info {
              padding: 16px;
              background: linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 100%);
              margin-top: -50px;
              position: relative;
              z-index: 1;
            }
            
            .curate-property-card-address {
              font-size: 0.95rem;
              font-weight: 600;
              color: #fff;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-bottom: 4px;
            }

            .curate-property-card-locality {
              font-size: 0.78rem;
              color: #9ca3af;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-bottom: 6px;
            }
            
            .curate-property-card-details {
              font-size: 0.75rem;
              color: #6b7280;
              margin-bottom: 4px;
            }
            
            .curate-property-card-price {
              font-size: 0.85rem;
              color: #4ade80;
              font-weight: 700;
            }
            
            .curate-property-check {
              position: absolute;
              top: 10px;
              right: 10px;
              width: 28px;
              height: 28px;
              border-radius: 50%;
              background: #1a1a1a;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #1a1a1a;
              font-size: 0.8rem;
              font-weight: bold;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
              animation: scaleIn 0.2s ease-out;
            }

            /* Details button — subtle pill on top-left of card */
            .curate-property-details-btn {
              position: absolute;
              top: 10px;
              left: 10px;
              padding: 5px 12px;
              border-radius: 999px;
              background: rgba(255, 255, 255, 0.92);
              color: #111;
              border: 1px solid rgba(0, 0, 0, 0.06);
              font-size: 0.72rem;
              font-weight: 600;
              letter-spacing: 0.02em;
              cursor: pointer;
              transition: all 0.12s;
              backdrop-filter: blur(6px);
              box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
              z-index: 2;
              opacity: 0;
            }

            .curate-property-card:hover .curate-property-details-btn {
              opacity: 1;
            }

            .curate-property-details-btn:hover {
              background: #ffffff;
              transform: translateY(-1px);
              box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
            }
            
            @keyframes scaleIn {
              from { transform: scale(0); }
              to { transform: scale(1); }
            }
            
            .curate-load-more {
              display: flex;
              justify-content: center;
              padding: 24px;
            }
            
            .curate-load-more-btn {
              padding: 14px 32px;
              border-radius: 12px;
              border: 1px solid rgba(0, 0, 0, 0.08);
              background: rgba(0, 0, 0, 0.03);
              color: #374151;
              font-size: 0.95rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              gap: 8px;
            }
            
            .curate-load-more-btn:hover:not(:disabled) {
              background: rgba(0, 0, 0, 0.06);
              border-color: #d1d5db;
              transform: translateY(-2px);
            }
            
            .curate-load-more-btn:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }
            
            .curate-modal-footer {
              padding: 20px 32px;
              background: rgba(0, 0, 0, 0.3);
              border-top: 1px solid rgba(0, 0, 0, 0.03);
              display: flex;
              justify-content: flex-end;
              gap: 12px;
            }
            
            .curate-btn {
              padding: 12px 24px;
              border-radius: 12px;
              font-size: 0.95rem;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              gap: 8px;
            }
            
            .curate-btn-secondary {
              background: rgba(0, 0, 0, 0.03);
              border: 1px solid rgba(0, 0, 0, 0.06);
              color: #6b7280;
            }
            
            .curate-btn-secondary:hover:not(:disabled) {
              background: rgba(0, 0, 0, 0.04);
              border-color: rgba(0, 0, 0, 0.1);
              color: #fff;
            }
            
            .curate-btn-primary {
              background: #4a4a4a;
              border: none;
              color: white;
              box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            }
            
            .curate-btn-primary:hover:not(:disabled) {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
              background: #5a5a5a;
            }
            
            .curate-btn:disabled {
              opacity: 0.5;
              cursor: not-allowed;
              transform: none !important;
            }
            
            /* ========== LIGHT MODE STYLES ========== */
            .light-mode .curate-matches-modal,
            .dashboard-container.light-mode .curate-matches-modal {
              background: rgba(0, 0, 0, 0.03) !important;
            }
            
            .light-mode .curate-modal-container,
            .dashboard-container.light-mode .curate-modal-container {
              background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%) !important;
              border: 1px solid rgba(0, 0, 0, 0.1) !important;
              box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.05) !important;
            }
            
            .light-mode .curate-modal-header,
            .dashboard-container.light-mode .curate-modal-header {
              background: rgba(0, 0, 0, 0.03) !important;
              border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
            }
            
            .light-mode .curate-modal-header::before,
            .dashboard-container.light-mode .curate-modal-header::before {
              background: radial-gradient(ellipse at top right, rgba(0, 0, 0, 0.02), transparent 60%) !important;
            }
            
            .light-mode .curate-header-info h2,
            .dashboard-container.light-mode .curate-header-info h2 {
              color: rgba(26, 26, 46, 0.5) !important;
            }
            
            .light-mode .curate-user-name,
            .dashboard-container.light-mode .curate-user-name {
              color: #1a1a2e !important;
            }
            
            .light-mode .curate-stat-pill,
            .dashboard-container.light-mode .curate-stat-pill {
              background: rgba(0, 0, 0, 0.06) !important;
              color: rgba(26, 26, 46, 0.8) !important;
            }
            
            .light-mode .curate-close-btn,
            .dashboard-container.light-mode .curate-close-btn {
              border: 1px solid rgba(0, 0, 0, 0.1) !important;
              background: rgba(0, 0, 0, 0.03) !important;
              color: rgba(26, 26, 46, 0.7) !important;
            }
            
            .light-mode .curate-close-btn:hover,
            .dashboard-container.light-mode .curate-close-btn:hover {
              background: rgba(0, 0, 0, 0.08) !important;
              border-color: rgba(0, 0, 0, 0.15) !important;
              color: #1a1a2e !important;
            }
            
            .light-mode .curate-tabs-container,
            .dashboard-container.light-mode .curate-tabs-container {
              background: transparent !important;
              border-bottom: 1px solid rgba(0, 0, 0, 0.08) !important;
            }
            
            .light-mode .curate-tab,
            .dashboard-container.light-mode .curate-tab {
              color: rgba(26, 26, 46, 0.5) !important;
              border: none !important;
              box-shadow: none !important;
              outline: none !important;
            }
            
            .light-mode .curate-tab svg,
            .dashboard-container.light-mode .curate-tab svg {
              color: rgba(26, 26, 46, 0.5) !important;
              stroke: rgba(26, 26, 46, 0.5) !important;
            }
            
            .light-mode .curate-tab:hover:not(.active),
            .dashboard-container.light-mode .curate-tab:hover:not(.active) {
              color: rgba(26, 26, 46, 0.8) !important;
              background: rgba(0, 0, 0, 0.03) !important;
            }
            
            .light-mode .curate-tab:hover:not(.active) svg,
            .dashboard-container.light-mode .curate-tab:hover:not(.active) svg {
              color: rgba(26, 26, 46, 0.8) !important;
              stroke: rgba(26, 26, 46, 0.8) !important;
            }
            
            .light-mode .curate-tab.active,
            .dashboard-container.light-mode .curate-tab.active {
              background: transparent !important;
              color: #1a1a2e !important;
              border: none !important;
              box-shadow: none !important;
            }
            
            .light-mode .curate-tab.active svg,
            .dashboard-container.light-mode .curate-tab.active svg {
              color: #1a1a2e !important;
              stroke: #1a1a2e !important;
            }
            
            .light-mode .curate-tab-badge,
            .dashboard-container.light-mode .curate-tab-badge {
              background: rgba(0, 0, 0, 0.08) !important;
              color: rgba(26, 26, 46, 0.7) !important;
            }
            
            .light-mode .curate-tab.active .curate-tab-badge,
            .dashboard-container.light-mode .curate-tab.active .curate-tab-badge {
              background: #4a4a4a !important;
              color: white !important;
            }
            
            .light-mode .curate-modal-body,
            .dashboard-container.light-mode .curate-modal-body {
              background: #ffffff !important;
            }
            
            .light-mode .curate-empty-title,
            .dashboard-container.light-mode .curate-empty-title {
              color: #1a1a2e !important;
            }
            
            .light-mode .curate-empty-subtitle,
            .dashboard-container.light-mode .curate-empty-subtitle {
              color: rgba(26, 26, 46, 0.6) !important;
            }
            
            .light-mode .curate-empty-icon,
            .dashboard-container.light-mode .curate-empty-icon {
              background: rgba(0, 0, 0, 0.06) !important;
              border: 1px solid rgba(0, 0, 0, 0.08) !important;
            }
            
            .light-mode .curate-empty-icon svg,
            .dashboard-container.light-mode .curate-empty-icon svg {
              color: rgba(0, 0, 0, 0.4) !important;
              stroke: rgba(0, 0, 0, 0.4) !important;
            }
            
            .light-mode .curate-match-card,
            .dashboard-container.light-mode .curate-match-card {
              background: linear-gradient(145deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.01) 100%) !important;
              border: 1px solid rgba(0, 0, 0, 0.08) !important;
            }
            
            .light-mode .curate-match-card:hover,
            .dashboard-container.light-mode .curate-match-card:hover {
              border-color: rgba(0, 0, 0, 0.15) !important;
              background: linear-gradient(145deg, rgba(0, 0, 0, 0.03) 0%, rgba(0, 0, 0, 0.01) 100%) !important;
            }
            
            .light-mode .curate-match-meta,
            .dashboard-container.light-mode .curate-match-meta {
              color: rgba(26, 26, 46, 0.6) !important;
            }
            
            .light-mode .curate-property-mini,
            .dashboard-container.light-mode .curate-property-mini {
              background: rgba(0, 0, 0, 0.02) !important;
              border: 1px solid rgba(0, 0, 0, 0.06) !important;
            }
            
            .light-mode .curate-property-mini:hover,
            .dashboard-container.light-mode .curate-property-mini:hover {
              border-color: rgba(0, 0, 0, 0.12) !important;
            }
            
            .light-mode .curate-property-mini-address,
            .dashboard-container.light-mode .curate-property-mini-address {
              color: #1a1a2e !important;
            }
            
            .light-mode .curate-property-mini-price,
            .dashboard-container.light-mode .curate-property-mini-price {
              color: #16a34a !important;
            }
            
            .light-mode .curate-search-label,
            .dashboard-container.light-mode .curate-search-label {
              color: rgba(26, 26, 46, 0.7) !important;
            }
            
            .light-mode .curate-refresh-btn,
            .dashboard-container.light-mode .curate-refresh-btn {
              border: 1px solid rgba(0, 0, 0, 0.15) !important;
              background: rgba(0, 0, 0, 0.04) !important;
              color: rgba(26, 26, 46, 0.8) !important;
            }
            
            .light-mode .curate-refresh-btn:hover:not(:disabled),
            .dashboard-container.light-mode .curate-refresh-btn:hover:not(:disabled) {
              background: rgba(0, 0, 0, 0.08) !important;
              border-color: rgba(0, 0, 0, 0.2) !important;
            }
            
            .light-mode .curate-search-input,
            .dashboard-container.light-mode .curate-search-input {
              border: 1px solid rgba(0, 0, 0, 0.1) !important;
              background: rgba(0, 0, 0, 0.02) !important;
              color: #1a1a2e !important;
            }
            
            .light-mode .curate-search-input:focus,
            .dashboard-container.light-mode .curate-search-input:focus {
              border-color: rgba(0, 0, 0, 0.25) !important;
              background: #ffffff !important;
              box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05) !important;
            }
            
            .light-mode .curate-search-input::placeholder,
            .dashboard-container.light-mode .curate-search-input::placeholder {
              color: rgba(26, 26, 46, 0.4) !important;
            }
            
            .light-mode .curate-selected-bar,
            .dashboard-container.light-mode .curate-selected-bar {
              background: rgba(0, 0, 0, 0.04) !important;
              border: 1px solid rgba(0, 0, 0, 0.1) !important;
            }
            
            .light-mode .curate-selected-count,
            .dashboard-container.light-mode .curate-selected-count {
              color: #1a1a2e !important;
            }
            
            .light-mode .curate-selected-label,
            .dashboard-container.light-mode .curate-selected-label {
              color: rgba(26, 26, 46, 0.6) !important;
            }
            
            .light-mode .curate-selected-dot,
            .dashboard-container.light-mode .curate-selected-dot {
              background: rgba(0, 0, 0, 0.15) !important;
            }
            
            .light-mode .curate-selected-dot.filled,
            .dashboard-container.light-mode .curate-selected-dot.filled {
              background: #4a4a4a !important;
            }
            
            .light-mode .curate-property-card,
            .dashboard-container.light-mode .curate-property-card {
              background: #ffffff !important;
              border: 2px solid rgba(0, 0, 0, 0.08) !important;
            }
            
            .light-mode .curate-property-card:hover,
            .dashboard-container.light-mode .curate-property-card:hover {
              border-color: rgba(0, 0, 0, 0.2) !important;
              box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1) !important;
            }
            
            .light-mode .curate-property-card.selected,
            .dashboard-container.light-mode .curate-property-card.selected {
              border-color: #4a4a4a !important;
              box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1), 0 12px 30px rgba(0, 0, 0, 0.15) !important;
            }
            
            .light-mode .curate-property-card-info,
            .dashboard-container.light-mode .curate-property-card-info {
              background: #ffffff !important;
              margin-top: 0 !important;
            }
            
            .light-mode .curate-property-card-address,
            .dashboard-container.light-mode .curate-property-card-address {
              color: #1a1a2e !important;
            }
            
            .light-mode .curate-property-card-details,
            .dashboard-container.light-mode .curate-property-card-details {
              color: rgba(26, 26, 46, 0.6) !important;
            }

            .light-mode .curate-property-card-locality,
            .dashboard-container.light-mode .curate-property-card-locality {
              color: #6b7280 !important;
            }
            
            .light-mode .curate-property-card-price,
            .dashboard-container.light-mode .curate-property-card-price {
              color: #111 !important;
            }
            
            .light-mode .curate-load-more-btn,
            .dashboard-container.light-mode .curate-load-more-btn {
              border: 1px solid rgba(0, 0, 0, 0.15) !important;
              background: rgba(0, 0, 0, 0.04) !important;
              color: rgba(26, 26, 46, 0.8) !important;
            }
            
            .light-mode .curate-load-more-btn:hover:not(:disabled),
            .dashboard-container.light-mode .curate-load-more-btn:hover:not(:disabled) {
              background: rgba(0, 0, 0, 0.08) !important;
              border-color: rgba(0, 0, 0, 0.2) !important;
            }
            
            .light-mode .curate-modal-footer,
            .dashboard-container.light-mode .curate-modal-footer {
              background: rgba(0, 0, 0, 0.02) !important;
              border-top: 1px solid rgba(0, 0, 0, 0.08) !important;
            }
            
            .light-mode .curate-btn-secondary,
            .dashboard-container.light-mode .curate-btn-secondary {
              background: rgba(0, 0, 0, 0.03) !important;
              border: 1px solid rgba(0, 0, 0, 0.1) !important;
              color: rgba(26, 26, 46, 0.7) !important;
            }
            
            .light-mode .curate-btn-secondary:hover:not(:disabled),
            .dashboard-container.light-mode .curate-btn-secondary:hover:not(:disabled) {
              background: rgba(0, 0, 0, 0.06) !important;
              border-color: rgba(0, 0, 0, 0.15) !important;
              color: #1a1a2e !important;
            }
            
            .light-mode .curate-delete-btn,
            .dashboard-container.light-mode .curate-delete-btn {
              border: 1px solid rgba(220, 38, 38, 0.2) !important;
              background: rgba(220, 38, 38, 0.06) !important;
              color: #dc2626 !important;
            }
            
            .light-mode .curate-delete-btn:hover,
            .dashboard-container.light-mode .curate-delete-btn:hover {
              background: rgba(220, 38, 38, 0.12) !important;
              border-color: rgba(220, 38, 38, 0.3) !important;
            }
            
            .light-mode .curate-status-badge.active,
            .dashboard-container.light-mode .curate-status-badge.active {
              background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%) !important;
              color: #16a34a !important;
              border: 1px solid rgba(34, 197, 94, 0.2) !important;
            }
            
            .light-mode .curate-status-badge.expired,
            .dashboard-container.light-mode .curate-status-badge.expired {
              background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%) !important;
              color: #dc2626 !important;
              border: 1px solid rgba(220, 38, 38, 0.2) !important;
            }
            
            .light-mode .curate-status-badge.completed,
            .dashboard-container.light-mode .curate-status-badge.completed {
              background: rgba(0, 0, 0, 0.06) !important;
              color: rgba(26, 26, 46, 0.7) !important;
              border: 1px solid rgba(0, 0, 0, 0.1) !important;
            }
            
            .light-mode .curate-property-check,
            .dashboard-container.light-mode .curate-property-check,
            .light-mode .curate-matches-modal .curate-property-check,
            .curate-matches-modal.light-mode .curate-property-check {
              background: #374151 !important;
              background-color: #374151 !important;
              background-image: none !important;
              color: #fff !important;
              -webkit-text-fill-color: #fff !important;
              box-shadow: 0 4px 12px rgba(55, 65, 81, 0.3) !important;
            }
            
            .light-mode .curate-btn-primary,
            .dashboard-container.light-mode .curate-btn-primary,
            .light-mode .curate-matches-modal .curate-btn-primary,
            .curate-matches-modal.light-mode .curate-btn-primary {
              background: #374151 !important;
              background-color: #374151 !important;
              background-image: none !important;
              color: #fff !important;
              -webkit-text-fill-color: #fff !important;
              box-shadow: 0 4px 15px rgba(55, 65, 81, 0.25) !important;
            }
            
            .light-mode .curate-btn-primary svg,
            .dashboard-container.light-mode .curate-btn-primary svg,
            .light-mode .curate-matches-modal .curate-btn-primary svg,
            .curate-matches-modal.light-mode .curate-btn-primary svg,
            .light-mode .curate-modal-footer .curate-btn-primary svg,
            .dashboard-container.light-mode .curate-modal-footer .curate-btn-primary svg {
              color: #fff !important;
              stroke: #fff !important;
              fill: none !important;
            }
            
            .light-mode .curate-btn-primary svg *,
            .dashboard-container.light-mode .curate-btn-primary svg * {
              color: #fff !important;
              stroke: #fff !important;
            }
            
            .light-mode .curate-btn-primary:hover:not(:disabled),
            .dashboard-container.light-mode .curate-btn-primary:hover:not(:disabled),
            .light-mode .curate-matches-modal .curate-btn-primary:hover:not(:disabled) {
              background: #1f2937 !important;
              background-color: #1f2937 !important;
              background-image: none !important;
              box-shadow: 0 6px 20px rgba(31, 41, 55, 0.35) !important;
            }
          `}</style>

          <div className="curate-modal-container">
            {/* Header */}
            <div className="curate-modal-header">
              <div className="curate-header-content">
                <div className="curate-header-left">
                  <div className="curate-header-info">
                    <h2>Curate Matches</h2>
                    <div className="curate-user-name">{buyer?.name}</div>
                  </div>
                </div>
                <div className="curate-header-right">
                  <span className="curate-stat-pill">
                    <Package size={14} /> {totalMatchesSent} matches sent
                  </span>
                  <button className="curate-close-btn" onClick={onClose}>
                    <X size={20} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Tabs */}
            <div className="curate-tabs-container">
              <div className="curate-tabs">
                <button 
                  className={`curate-tab ${curateView === 'create' ? 'active' : ''}`}
                  onClick={() => setCurateView('create')}
                >
                  <Plus size={16} />
                  Create New
                </button>
                <button 
                  className={`curate-tab ${curateView === 'current' ? 'active' : ''}`}
                  onClick={() => setCurateView('current')}
                >
                  <Clock size={16} />
                  Current
                  <span className="curate-tab-badge">{existingMatches.length}</span>
                </button>
                <button 
                  className={`curate-tab ${curateView === 'history' ? 'active' : ''}`}
                  onClick={() => setCurateView('history')}
                >
                  <Inbox size={16} />
                  History
                  <span className="curate-tab-badge">{matchHistory.length}</span>
                </button>
              </div>
            </div>
            
            {/* Body */}
            <div className="curate-modal-body">
              {/* Current Matches View */}
              {curateView === 'current' && (
                <div>
                  {existingMatches.length === 0 ? (
                    <div className="curate-empty-state">
                      <div className="curate-empty-icon">
                        <Package size={36} color="#d1d5db" />
                      </div>
                      <div className="curate-empty-title">No Active Matches</div>
                      <div className="curate-empty-subtitle">
                        This user doesn't have any active curated matches. Click "Create New" to send them personalized property recommendations.
                      </div>
                    </div>
                  ) : (
                    existingMatches.map(match => {
                      const now = new Date();
                      const expiresAt = new Date(match.expiresAt);
                      const daysLeft = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
                      const viewedCount = match.viewedProperties?.length || 0;
                      
                      return (
                        <div key={match._id} className="curate-match-card">
                          <div className="curate-match-header">
                            <div className="curate-match-stats">
                              <span className="curate-status-badge active">
                                <Clock size={14} />
                                {daysLeft}d left
                              </span>
                              <span className="curate-match-meta">
                                <Package size={14} /> {match.properties.length} properties
                              </span>
                              <span className="curate-match-meta">
                                <Eye size={14} /> {viewedCount} / {match.properties.length} viewed
                              </span>
                              <span className="curate-match-meta">
                                <Calendar size={14} /> {new Date(match.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <button 
                              className="curate-delete-btn"
                              onClick={() => handleDeleteMatch(match._id)}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                          <div className="curate-properties-grid">
                            {match.properties.map(property => (
                              <div key={property._id} className="curate-property-mini">
                                <img
                                  src={property.photos?.[0] || property.mainImage || 'https://via.placeholder.com/200x120?text=No+Image'}
                                  alt={property.address}
                                />
                                <div className="curate-property-mini-info">
                                  <div className="curate-property-mini-address">{property.address}</div>
                                  <div className="curate-property-mini-price">
                                    {typeof property.price === 'number' ? `$${property.price.toLocaleString()}` : property.price}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* History View */}
              {curateView === 'history' && (
                <div>
                  {matchHistory.length === 0 ? (
                    <div className="curate-empty-state">
                      <div className="curate-empty-icon">
                        <Calendar size={36} color="#d1d5db" />
                      </div>
                      <div className="curate-empty-title">No Match History</div>
                      <div className="curate-empty-subtitle">
                        Previous matches will appear here once they expire or are completed by the user.
                      </div>
                    </div>
                  ) : (
                    matchHistory.map(match => {
                      const isCompleted = match.status === 'completed';
                      const viewedCount = match.viewedProperties?.length || 0;
                      
                      return (
                        <div key={match._id} className="curate-match-card history">
                          <div className="curate-match-header">
                            <div className="curate-match-stats">
                              <span className={`curate-status-badge ${isCompleted ? 'completed' : 'expired'}`}>
                                {isCompleted ? <Check size={14} /> : <Clock size={14} />}
                                {isCompleted ? 'Completed' : 'Expired'}
                              </span>
                              <span className="curate-match-meta">
                                <Package size={14} /> {match.properties.length} properties
                              </span>
                              <span className="curate-match-meta">
                                <Eye size={14} /> {viewedCount} / {match.properties.length} viewed
                              </span>
                              <span className="curate-match-meta">
                                <Calendar size={14} /> {new Date(match.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <div className="curate-properties-grid">
                            {match.properties.map(property => (
                              <div key={property._id} className="curate-property-mini" style={{ opacity: 0.6 }}>
                                <img
                                  src={property.photos?.[0] || property.mainImage || 'https://via.placeholder.com/200x120?text=No+Image'}
                                  alt={property.address}
                                />
                                <div className="curate-property-mini-info">
                                  <div className="curate-property-mini-address">{property.address}</div>
                                  <div className="curate-property-mini-price">
                                    {typeof property.price === 'number' ? `$${property.price.toLocaleString()}` : property.price}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              {/* Create New Match View */}
              {curateView === 'create' && (
                <div>
                  {/* Mode toggle: Algorithm vs Search */}
                  <div className="curate-mode-toggle">
                    <button
                      type="button"
                      className={`curate-mode-pill ${createMode === 'algorithm' ? 'active' : ''}`}
                      onClick={() => setCreateMode('algorithm')}
                    >
                      Algorithm-Ranked
                    </button>
                    <button
                      type="button"
                      className={`curate-mode-pill ${createMode === 'search' ? 'active' : ''}`}
                      onClick={() => setCreateMode('search')}
                    >
                      <Search size={14} style={{ marginRight: '6px' }} />
                      Search All Listings
                    </button>
                  </div>

                  {/* ─── Algorithm sub-view: existing behavior ─── */}
                  {createMode === 'algorithm' && (
                    <div className="curate-search-section">
                      <div className="curate-search-header">
                        <span className="curate-search-label">
                          Algorithm-Ranked Properties ({filteredProperties.length} of {propertiesTotal} loaded)
                        </span>
                      </div>
                      <input
                        type="text"
                        className="curate-search-input"
                        value={propertySearchTerm}
                        onChange={(e) => handlePropertySearch(e.target.value)}
                        placeholder="Search by address or neighborhood..."
                      />
                    </div>
                  )}

                  {/* ─── Search sub-view: filter form ─── */}
                  {createMode === 'search' && searchForm && (
                    <div className="curate-search-form">
                      {/* Prefill banner — orients the agent to where these values came from */}
                      <div className="curate-prefill-banner">
                        <div className="curate-prefill-banner-text">
                          <span className="curate-prefill-banner-icon">✦</span>
                          <span>
                            {formMatchesUserPrefs ? (
                              <>
                                Filters pre-filled from{' '}
                                <strong>{buyer?.name?.split(' ')[0] || 'this user'}'s</strong>{' '}
                                saved preferences.
                              </>
                            ) : (
                              <>Filters cleared. Searching with no preference constraints.</>
                            )}
                          </span>
                        </div>
                        <div className="curate-prefill-banner-actions">
                          {formMatchesUserPrefs ? (
                            <button
                              type="button"
                              className="curate-prefill-btn curate-prefill-btn-danger"
                              onClick={() => setSearchForm(buildInitialSearchForm(null))}
                              title="Clear all filters"
                            >
                              Clear all
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="curate-prefill-btn"
                              onClick={() => setSearchForm(buildInitialSearchForm(buyer))}
                              title="Restore the user's saved filter preferences"
                            >
                              Reset to User's Preferences
                            </button>
                          )}
                        </div>
                      </div>

                      {/* ─── Address lookup (outranks every filter below) ─── */}
                      <div className="curate-addr-lookup" ref={addressBoxRef}>
                        <div className="curate-addr-lookup-head">
                          <span className="curate-addr-lookup-title">
                            <MapPin size={12} style={{ marginRight: '5px', verticalAlign: '-1px' }} />
                            Know the address?
                          </span>
                          <span className="curate-addr-lookup-sub">
                            Searches that address directly — the filters below don't apply
                          </span>
                        </div>

                        <div className="curate-addr-lookup-row">
                          <div className="curate-addr-input-wrap">
                            <input
                              type="text"
                              className="curate-addr-input"
                              value={addressQuery}
                              onChange={(e) => onAddressChange(e.target.value)}
                              onKeyDown={onAddressKeyDown}
                              placeholder="4521 Vine St, Denver, CO"
                              autoComplete="off"
                            />

                            {suggestions.length > 0 && (
                              <div className="curate-addr-suggestions">
                                {suggestions.map((s, i) => (
                                  <button
                                    key={`${s.title}-${i}`}
                                    type="button"
                                    className={`curate-addr-suggestion ${i === highlightedSuggestion ? 'highlighted' : ''}`}
                                    onMouseEnter={() => setHighlightedSuggestion(i)}
                                    onClick={() => pickSuggestion(s)}
                                  >
                                    <MapPin size={11} className="curate-addr-suggestion-icon" />
                                    <span>{s.title}</span>
                                  </button>
                                ))}
                                {/* Suggestions come from the public-record dataset, not the
                                    MLS one — a match here does NOT mean an active listing. */}
                                <div className="curate-addr-suggestions-note">
                                  Covers all known addresses. Not every one has an active listing.
                                </div>
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            className={`curate-addr-find-btn ${!mlsAcknowledged ? 'gated' : ''}`}
                            onClick={runAddressLookup}
                            disabled={isLookingUp || !addressQuery.trim()}
                            title={!mlsAcknowledged ? 'Please acknowledge the MLS notice to search' : undefined}
                          >
                            {isLookingUp ? 'Looking…' : <><Search size={13} /> Find</>}
                          </button>

                          {(addressQuery || lookupSource) && (
                            <button
                              type="button"
                              className="curate-addr-clear-btn"
                              onClick={clearAddressLookup}
                            >
                              Clear
                            </button>
                          )}
                        </div>

                        {lookupNotice && (
                          <div className={`curate-addr-notice ${lookupNotice.tone}`}>
                            {lookupNotice.text}
                          </div>
                        )}

                        {lookupSource && !lookupNotice && (
                          <div className="curate-addr-notice source">
                            {lookupSource === 'mls'
                              ? 'Pulled live from the MLS just now.'
                              : 'Found in our listings.'}
                          </div>
                        )}
                      </div>

                      <div className="curate-search-form-row">
                        <input
                          type="text"
                          className="curate-search-input"
                          value={searchForm.search}
                          onChange={(e) => updateSearchField('search', e.target.value)}
                          placeholder="Search description or keyword..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') tryFetchSearch(1, false);
                          }}
                        />
                        <button
                          type="button"
                          className={`curate-search-go-btn ${!mlsAcknowledged ? 'gated' : ''}`}
                          onClick={() => tryFetchSearch(1, false)}
                          disabled={isSearching}
                          title={!mlsAcknowledged ? 'Please acknowledge the MLS notice to search' : undefined}
                        >
                          {isSearching ? 'Searching…' : <><Search size={14} /> Search</>}
                        </button>
                      </div>

                      {/* Top MLS acknowledgement (synced with the bottom one via mlsAcknowledged state) */}
                      <label className={`curate-mls-disclaimer ${mlsHighlighted ? 'highlighted' : ''}`}>
                        <input
                          type="checkbox"
                          checked={mlsAcknowledged}
                          onChange={(e) => setMlsAcknowledged(e.target.checked)}
                        />
                        <span>
                          I understand that this search may not reflect real-time MLS inventory
                          or updates. When real-time accuracy is critical, the MLS remains the
                          most current and complete source of available listings.
                        </span>
                      </label>

                      <div className="curate-search-form-grid">
                        <label className="curate-search-field">
                          <span>Listing Type</span>
                          <select value={searchForm.listingType} onChange={(e) => updateSearchField('listingType', e.target.value)}>
                            <option value="purchase">For Sale</option>
                            <option value="rent">For Rent</option>
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Min Price</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatNumberInput(searchForm.minPrice)}
                            onChange={(e) => updateSearchField('minPrice', parseNumberInput(e.target.value))}
                            placeholder="Any"
                          />
                        </label>

                        <label className="curate-search-field">
                          <span>Max Price</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={formatNumberInput(searchForm.maxPrice)}
                            onChange={(e) => updateSearchField('maxPrice', parseNumberInput(e.target.value))}
                            placeholder="Any"
                          />
                        </label>

                        <label className="curate-search-field">
                          <span>Min Beds</span>
                          <select value={searchForm.minBedrooms} onChange={(e) => updateSearchField('minBedrooms', e.target.value)}>
                            <option value="">Any</option>
                            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}+</option>)}
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Min Baths</span>
                          <select value={searchForm.minBathrooms} onChange={(e) => updateSearchField('minBathrooms', e.target.value)}>
                            <option value="">Any</option>
                            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}+</option>)}
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Property Type</span>
                          <select value={searchForm.propertyType} onChange={(e) => updateSearchField('propertyType', e.target.value)}>
                            <option value="any">Any</option>
                            <option value="House">House</option>
                            <option value="Condo">Condo</option>
                            <option value="Apartment">Apartment</option>
                            <option value="Townhouse">Townhouse</option>
                            <option value="Multifamily">Multifamily</option>
                            <option value="ManufacturedMobile">Manufactured/Mobile</option>
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Min Sqft</span>
                          <select value={searchForm.minSqft} onChange={(e) => updateSearchField('minSqft', e.target.value)}>
                            {MIN_SQFT_OPTIONS.map(o => (
                              <option key={o.value || 'any'} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Max Sqft</span>
                          <select value={searchForm.maxSqft} onChange={(e) => updateSearchField('maxSqft', e.target.value)}>
                            {MAX_SQFT_OPTIONS.map(o => (
                              <option key={o.value || 'any'} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Year Built</span>
                          <select value={searchForm.yearBuilt} onChange={(e) => updateSearchField('yearBuilt', e.target.value)}>
                            {YEAR_BUILT_OPTIONS.map(o => (
                              <option key={o.value || 'any'} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Min Garage Spaces</span>
                          <select value={searchForm.garage} onChange={(e) => updateSearchField('garage', e.target.value)}>
                            <option value="">Any</option>
                            {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}+</option>)}
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Min Lot Size</span>
                          <select value={searchForm.minLotSize} onChange={(e) => updateSearchField('minLotSize', e.target.value)}>
                            {MIN_LOT_SIZE_OPTIONS.map(o => (
                              <option key={o.value || 'any'} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Max Lot Size</span>
                          <select value={searchForm.maxLotSize} onChange={(e) => updateSearchField('maxLotSize', e.target.value)}>
                            {MAX_LOT_SIZE_OPTIONS.map(o => (
                              <option key={o.value || 'any'} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>HOA</span>
                          <select value={searchForm.hoaPreference} onChange={(e) => updateSearchField('hoaPreference', e.target.value)}>
                            <option value="any">Any</option>
                            <option value="yes">Required</option>
                            <option value="no">No HOA</option>
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Pool</span>
                          <select value={searchForm.poolPreference} onChange={(e) => updateSearchField('poolPreference', e.target.value)}>
                            <option value="any">Any</option>
                            <option value="yes">Required</option>
                            <option value="no">No</option>
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Fireplace</span>
                          <select value={searchForm.fireplacePreference} onChange={(e) => updateSearchField('fireplacePreference', e.target.value)}>
                            <option value="any">Any</option>
                            <option value="yes">Required</option>
                            <option value="no">No</option>
                          </select>
                        </label>

                        <label className="curate-search-field">
                          <span>Min School Rating</span>
                          <select value={searchForm.minSchoolRating} onChange={(e) => updateSearchField('minSchoolRating', e.target.value)}>
                            <option value="">Any</option>
                            {[5, 6, 7, 8, 9].map(n => <option key={n} value={n}>{n}+ / 10</option>)}
                          </select>
                        </label>

                        <label className="curate-search-field curate-search-field-checkbox">
                          <input type="checkbox" checked={searchForm.singleStory} onChange={(e) => updateSearchField('singleStory', e.target.checked)} />
                          <span>Single Story Only</span>
                        </label>
                      </div>

                      {/* ─── Location filter (Cities OR Map area — pick one) ─── */}
                      <div className="curate-cities-section" ref={citiesDropdownRef}>
                        <div className="curate-cities-label-row">
                          <span className="curate-cities-label">Location</span>
                          {/* Right-aligned helpers — only show in the matching mode */}
                          {!searchForm.areaPolygon && searchForm.cities.length > 0 && (
                            <button
                              type="button"
                              className="curate-cities-action-btn"
                              onClick={() => updateSearchField('cities', [])}
                            >
                              Clear all
                            </button>
                          )}
                        </div>

                        {/* Two-pill mode toggle. Active mode is derived from
                            whether a polygon is set — no separate state. */}
                        <div className="curate-loc-mode">
                          <button
                            type="button"
                            className={`curate-loc-mode-pill ${!searchForm.areaPolygon ? 'active' : ''}`}
                            onClick={() => {
                              // Clearing the polygon flips back to cities mode.
                              if (searchForm.areaPolygon) {
                                setSearchForm(prev => prev ? {
                                  ...prev,
                                  areaPolygon: null,
                                  areaSavedId: null
                                } : prev);
                              }
                            }}
                          >
                            Cities
                          </button>
                          <button
                            type="button"
                            className={`curate-loc-mode-pill ${searchForm.areaPolygon ? 'active' : ''}`}
                            onClick={() => setShowMapPicker(true)}
                          >
                            <MapPin size={11} style={{ marginRight: '4px', verticalAlign: '-1px' }} />
                            Map area
                          </button>
                        </div>

                        {searchForm.areaPolygon ? (
                          // ─── Map area mode ────────────────────────────────
                          <div className="curate-loc-area-active">
                            <div className="curate-loc-area-pill">
                              <MapPin size={12} style={{ marginRight: '6px', flexShrink: 0 }} />
                              <span className="curate-loc-area-pill-label">
                                {searchForm.areaSavedId ? 'Saved area' : 'Custom map area'}
                              </span>
                              <button
                                type="button"
                                className="curate-loc-area-edit"
                                onClick={() => setShowMapPicker(true)}
                                title="Edit this area on the map"
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        ) : (
                        <>
                        <div
                          className={`curate-cities-trigger ${citiesDropdownOpen ? 'open' : ''}`}
                          onClick={() => {
                            if (availableCities.length === 0) return;
                            setCitiesDropdownOpen(o => !o);
                            setCityFilterText('');
                          }}
                        >
                          {searchForm.cities.length === 0 ? (
                            <span className="curate-cities-trigger-placeholder">
                              {isLoadingTerritories
                                ? 'Loading your territories…'
                                : territoriesError
                                  ? territoriesError
                                  : availableCities.length === 0
                                    ? "You haven't claimed any territories yet"
                                    : 'Select cities to search…'}
                            </span>
                          ) : (
                            <div className="curate-cities-trigger-tags">
                              {searchForm.cities.map(c => (
                                <span key={c} className="curate-cities-tag">
                                  {c}
                                  <button
                                    type="button"
                                    className="curate-cities-tag-x"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleCity(c);
                                    }}
                                    aria-label={`Remove ${c}`}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <ChevronDown
                            size={16}
                            className="curate-cities-trigger-chev"
                            style={{
                              transform: citiesDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.15s ease',
                            }}
                          />
                        </div>

                        {citiesDropdownOpen && availableCities.length > 0 && (
                          <div className="curate-cities-dropdown">
                            <input
                              type="text"
                              className="curate-cities-dropdown-search"
                              placeholder="Type to filter…"
                              value={cityFilterText}
                              onChange={(e) => setCityFilterText(e.target.value)}
                              autoFocus
                            />
                            <div className="curate-cities-dropdown-actions">
                              <button
                                type="button"
                                className="curate-cities-action-btn"
                                onClick={() => {
                                  // Select all currently-visible (filtered) cities
                                  const next = Array.from(
                                    new Set([...searchForm.cities, ...filteredAvailableCities])
                                  );
                                  updateSearchField('cities', next);
                                }}
                                disabled={
                                  filteredAvailableCities.length === 0 ||
                                  filteredAvailableCities.every(c => searchForm.cities.includes(c))
                                }
                              >
                                Select shown
                              </button>
                              <button
                                type="button"
                                className="curate-cities-action-btn"
                                onClick={() => updateSearchField('cities', [])}
                                disabled={searchForm.cities.length === 0}
                              >
                                Clear all
                              </button>
                            </div>
                            <div className="curate-cities-dropdown-list">
                              {filteredAvailableCities.length === 0 ? (
                                <div className="curate-cities-dropdown-empty">
                                  No cities match "{cityFilterText}"
                                </div>
                              ) : (
                                filteredAvailableCities.map(city => {
                                  const checked = searchForm.cities.includes(city);
                                  return (
                                    <label
                                      key={city}
                                      className={`curate-cities-dropdown-item ${checked ? 'checked' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleCity(city)}
                                      />
                                      <span>{city}</span>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                        </>
                        )}
                      </div>

                      <label className={`curate-mls-disclaimer ${mlsHighlighted ? 'highlighted' : ''}`}>
                        <input
                          type="checkbox"
                          checked={mlsAcknowledged}
                          onChange={(e) => setMlsAcknowledged(e.target.checked)}
                        />
                        <span>
                          I understand that this search may not reflect real-time MLS inventory
                          or updates. When real-time accuracy is critical, the MLS remains the
                          most current and complete source of available listings.
                        </span>
                      </label>

                      {/* Bottom Search button — mirrors the top one for ergonomics */}
                      <div className="curate-search-form-row" style={{ justifyContent: 'flex-end', marginTop: '16px', marginBottom: 0 }}>
                        <button
                          type="button"
                          className={`curate-search-go-btn ${!mlsAcknowledged ? 'gated' : ''}`}
                          onClick={() => tryFetchSearch(1, false)}
                          disabled={isSearching}
                          title={!mlsAcknowledged ? 'Please acknowledge the MLS notice to search' : undefined}
                        >
                          {isSearching ? 'Searching…' : <><Search size={14} /> Search</>}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Scroll anchor — used by tryFetchSearch to scroll to results after a successful search */}
                  <div ref={resultsAnchorRef} aria-hidden="true" />

                  {selectedProperties.length > 0 && (
                    <div className="curate-selected-bar">
                      <div className="curate-selected-info">
                        <span className="curate-selected-count">{selectedProperties.length}</span>
                        <span className="curate-selected-label">of 6 properties selected</span>
                      </div>
                      <div className="curate-selected-dots">
                        {[...Array(6)].map((_, i) => (
                          <div 
                            key={i} 
                            className={`curate-selected-dot ${i < selectedProperties.length ? 'filled' : ''}`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="curate-create-grid">
                    {displayedProperties.map(property => {
                      const isSelected = selectedProperties.find(p => p._id === property._id);
                      const propIdStr = property._id?.toString?.() || property._id;
                      const swipeStatus =
                        likedIds.has(propIdStr) ? 'liked' :
                        dislikedIds.has(propIdStr) ? 'passed' :
                        null;
                      return (
                        <div
                          key={property._id}
                          className={`curate-property-card ${isSelected ? 'selected' : ''}`}
                          onClick={() => togglePropertySelection(property)}
                        >
                          <img
                            src={property.mainImage || property.photos?.[0] || 'https://via.placeholder.com/200x130?text=No+Image'}
                            alt={property.address}
                          />
                          {swipeStatus && (
                            <div className={`curate-swipe-badge curate-swipe-badge-${swipeStatus}`}>
                              {swipeStatus === 'liked' ? '❤️ Liked' : '✕ Passed'}
                            </div>
                          )}
                          <button
                            type="button"
                            className="curate-property-details-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPropertyDetailsClick(property);
                            }}
                          >
                            Details
                          </button>
                          <div className="curate-property-card-info">
                            <div className="curate-property-card-address">{property.address}</div>
                            {property.csz && (
                              <div className="curate-property-card-locality">{property.csz}</div>
                            )}
                            {(property.beds || property.baths || property.bedrooms || property.bathrooms) && (
                              <div className="curate-property-card-details">
                                {property.beds || property.bedrooms} bed • {property.baths || property.bathrooms} bath
                              </div>
                            )}
                            <div className="curate-property-card-price">{property.price}</div>
                          </div>
                          {isSelected && (
                            <div className="curate-property-check">✓</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {displayedPage < displayedTotalPages && displayedProperties.length > 0 && (
                    <div className="curate-load-more">
                      <button
                        className="curate-load-more-btn"
                        onClick={isSearchMode ? loadMoreSearchResults : loadMoreProperties}
                        disabled={isDisplayedLoading}
                      >
                        {isDisplayedLoading ? (
                          <>Loading...</>
                        ) : (
                          <>
                            <ChevronDown size={18} />
                            Load More ({displayedTotal - displayedProperties.length} remaining)
                          </>
                        )}
                      </button>
                    </div>
                  )}
                  
                  {displayedProperties.length === 0 && !isDisplayedLoading && (
                    <div className="curate-empty-state">
                      <div className="curate-empty-icon">
                        <Search size={36} color="#d1d5db" />
                      </div>
                      <div className="curate-empty-title">
                        {isSearchMode && !hasSearchedYet ? 'Run a Search' : 'No Properties Found'}
                      </div>
                      <div className="curate-empty-subtitle">
                        {isSearchMode && !hasSearchedYet
                          ? 'Adjust the filters above and hit Search to see all matching listings.'
                          : 'Try adjusting your filters or refresh to load more properties.'}
                      </div>
                    </div>
                  )}
                  
                  {displayedProperties.length === 0 && isDisplayedLoading && (
                    <div className="curate-empty-state">
                      <div className="curate-empty-icon">
                        <RefreshCw size={36} color="#d1d5db" className="animate-spin" />
                      </div>
                      <div className="curate-empty-title">
                        {isSearchMode ? 'Searching…' : 'Loading Properties...'}
                      </div>
                      <div className="curate-empty-subtitle">
                        {isSearchMode
                          ? 'Querying all listings against your filters.'
                          : "Finding the best matches for this user's preferences."}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="curate-modal-footer">
              <button 
                className="curate-btn curate-btn-secondary"
                onClick={onClose}
                disabled={isCurating}
              >
                Close
              </button>
              {curateView === 'create' && (
                <button
                  className="curate-btn curate-btn-primary"
                  onClick={handleSendCuratedMatch}
                  disabled={isCurating || selectedProperties.length === 0}
                >
                  {isCurating ? (
                    <>Sending...</>
                  ) : (
                    <>
                      <Send size={16} />
                      Send {selectedProperties.length} {selectedProperties.length === 1 ? 'Match' : 'Matches'}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ─── Map area picker (overlays this modal when open) ─── */}
          <MapAreaPickerModal
            isOpen={showMapPicker}
            onClose={() => setShowMapPicker(false)}
            onApply={({ geometry, savedAreaId }) => {
              // Build the next form synchronously so we can both (a) commit it
              // to state and (b) hand it to fetchSearchResults immediately.
              // setSearchForm queues an update — reading state inside the same
              // tick would still see the old form, so we use the explicit
              // override path on fetchSearchResults instead.
              const nextForm = searchForm ? {
                ...searchForm,
                areaPolygon: geometry,
                areaSavedId: savedAreaId || null,
                // Switching to map mode clears any cities selection — they'd
                // be a redundant intersection on top of the polygon, and the
                // location-mode toggle visually represents an either/or.
                cities: []
              } : searchForm;
              setSearchForm(nextForm);
              // Auto-trigger a search so results update as the modal closes.
              // Respect the MLS-acknowledgement gate — same behavior as if the
              // agent had clicked the Search button manually.
              if (mlsAcknowledged && nextForm) {
                fetchSearchResults(1, false, nextForm);
              } else {
                setMlsHighlighted(true);
                setTimeout(() => setMlsHighlighted(false), 1600);
              }
            }}
            claimedCities={availableCities}
            currentPolygon={searchForm?.areaPolygon || null}
          />

    </div>
  ), portalTarget);
};

export default CurateMatchesModal;